import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Pill,
  Plus,
  Trash2,
  FileDown,
  Settings,
  ChevronDown,
  ChevronUp,
  GripVertical,
  ShieldCheck,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { useConfigDocumentoSalva } from "../hooks/useConfigDocumentoSalva";
import {
  gerarPDFReceituario,
  gerarPDFReceituarioComControle,
  gerarPDFReceitaAntimicrobiano,
  montarReceituarioHTML,
  montarReceituarioComControleHTML,
  montarReceitaAntimicrobianoHTML,
  REGIME_ANTIMICROBIANO,
  REGIME_GLP1,
} from "../services/gerarPDF";
import ModalAssinatura from "./ModalAssinatura";
import { DadosAssinaturaWidget } from "../services/assinaturaCliente";
import {
  montarSeloAssinaturaDigital,
  dataHoraSeloAgora,
} from "../services/seloAssinatura";
import {
  gerarCodigoValidacao,
  montarBlocoQR,
  urlValidacao,
} from "../../validacoes/services/validacaoCliente";
import { montarFolhaOficialSncr } from "../services/gerarRceEletronica";
import {
  ambienteSncr,
  cnpjPlataforma,
  definirModoTeste,
  formatarCnpj,
  LIMIAR_SALDO_BAIXO,
  numeracaoNasAssinaturas,
} from "../../sncr/configSncr";
import { consumirNumero, lerCofre } from "../../sncr/cofreNumeracao";
import { abrirObtencaoPeloApp } from "../../sncr/ticketSncr";
import { TipoNumeracao } from "../../sncr/numeracaoSncr";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import {
  buscarMedicamentos,
  MedicamentoCatalogo,
} from "../data/medicamentosCatalogo";
import {
  classificarMedicamento,
  exigeTalonario,
  NOME_DOCUMENTO,
  ClassificacaoControlado,
} from "../data/substanciasControladas";
import {
  detectarAntimicrobiano,
  detectarAntimicrobianos,
} from "../data/antimicrobianos";
import {
  detectarAnalogoGLP1,
  detectarAnalogosGLP1,
} from "../data/analogosGLP1";
import {
  medicamentoExigeLME,
  medicamentoLMEehDislipidemia,
  medicamentoExigeHepatiteB,
  MEDICAMENTOS_HBV_CAMPO34,
} from "../data/formulariosAuxiliares";
import {
  montarFragmentoLME,
  DadosLME,
  SolicitanteLME,
  OPCOES_RACA_COR,
  OPCOES_PREENCHIDO_POR,
} from "../services/gerarFichaLME";
import {
  montarFragmentoHepatiteB,
  DadosHepatiteB,
  SolicitanteHepatiteB,
  CIDS_HBV,
  VALIDADE_OPCOES,
  COINFECCOES_OPCOES,
  INDICACOES_TERAPEUTICAS,
  SITUACAO_PROFILAXIA_OPCOES,
  RISCO_REATIVACAO_OPCOES,
  MOMENTO_PROFILAXIA_OPCOES,
  RISCO_REINFECCAO_OPCOES,
  BETA_HCG_OPCOES,
  OUTROS_CRITERIOS_OPCOES,
  CONDICOES_OPCOES,
  CONTRAINDICACAO_OPCOES,
  RESISTENCIA_OPCOES,
  USO_PREVIO_OPCOES,
} from "../services/gerarFichaHepatiteB";
import { useSorologias } from "../../sorologias/context/SorologiasContext";

// B212 (AI_131): GO-LIVE DE PRODUÇÃO CONCLUÍDO (29/06/2026) → assinatura habilitada
// para todos os médicos (o gate `ehMedico` abaixo mantém não-médicos fora). Antes
// ficava atrás da flag local `assinar_habilitado`, usada só nos navegadores de teste.
const ASSINATURA_HABILITADA = true;

// B212 F4 (AI_126) / B306 (AI_140): configuração de um documento a assinar. Usada
// pelo modal atual e pela fila (caso misto: 2 documentos assinados em sequência).
type ConfigModalAssinatura = {
  html: string;
  tipo: string;
  prescricaoId: string;
  dados: DadosAssinaturaWidget;
  nomeArquivo: string;
  codigoValidacao?: string;
  dadosValidacao?: {
    medicoNome: string;
    registro: string;
    pacienteTelefone: string;
    pacienteNascimento: string;
  };
};

// Diagnósticos comuns em hepatologia/transplante → CID-10 (atalho do modal LME).
const DIAGNOSTICOS_LME: { rotulo: string; cid: string }[] = [
  { rotulo: "Fígado transplantado", cid: "Z94.4" },
  { rotulo: "Rim transplantado", cid: "Z94.0" },
  { rotulo: "Outros órgãos e tecidos transplantados (tx de pâncreas)", cid: "Z94.8" },
  { rotulo: "Colangite biliar primária", cid: "K74.3" },
  { rotulo: "Colangite esclerosante primária", cid: "K83.0" },
  { rotulo: "Hepatite autoimune", cid: "K75.4" },
  { rotulo: "Cirrose hepática", cid: "K74.6" },
];

// B280 (AI_120) — dislipidemias (E78x): atalho de diagnóstico do LME quando o
// medicamento é estatina/hipolipemiante. CIDs conforme a tabela do SUS.
const DIAGNOSTICOS_LME_DISLIPIDEMIA: { rotulo: string; cid: string }[] = [
  { rotulo: "Hipercolesterolemia pura", cid: "E78.0" },
  { rotulo: "Hipergliceridemia pura", cid: "E78.1" },
  { rotulo: "Hiperlipidemia mista", cid: "E78.2" },
  { rotulo: "Hiperquilomicronemia", cid: "E78.3" },
  { rotulo: "Outras hiperlipidemias", cid: "E78.4" },
  { rotulo: "Hiperlipidemia não especificada", cid: "E78.5" },
  { rotulo: "Deficiências de lipoproteínas", cid: "E78.6" },
  { rotulo: "Outros distúrbios do metabolismo de lipoproteínas", cid: "E78.8" },
];

// Interpretação de resultados de sorologia (texto livre) → opções da ficha de
// Hepatite B. Conservador: só decide quando o texto é claro, senão deixa vazio
// para o médico escolher.
const interpretarReagente = (v?: string): "Reagente" | "Não Reagente" | "" => {
  if (!v) return "";
  const t = v.toLowerCase();
  if (t.includes("não") || t.includes("nao") || t.includes("negativ")) return "Não Reagente";
  if (t.includes("reagente") || t.includes("positiv") || t.includes("detect")) return "Reagente";
  return "";
};
const interpretarDetectavel = (v?: string): "Detectável" | "Não detectável" | "" => {
  if (!v) return "";
  const t = v.toLowerCase();
  if (t.includes("não") || t.includes("nao") || t.includes("indetect")) return "Não detectável";
  if (t.includes("detect") || t.includes("positiv")) return "Detectável";
  return "";
};

interface Medicamento {
  id: string;
  nome: string;
  posologia: string;
  tipoUso: "continuo" | "quantidade";
  quantidade?: string;
}

interface ReceituarioProps {
  paciente: Paciente;
  dadosIniciais?: any;
  onPrescricaoSalva?: () => void;
}
export default function Receituario({
  paciente,
  dadosIniciais,
  onPrescricaoSalva,
}: ReceituarioProps) {
  const { usuarioLogado } = useAuth();
  const [medicamentos, setMedicamentos] = useState<Medicamento[]>([]);

  // B356 (AI_154) — SALDO de numeração do SNCR do médico: alimenta o botão
  // "Obter/Renovar numeração" junto do "Assinar digitalmente" e o aviso de
  // estoque baixo. Relê quando a janela volta ao foco (ex.: depois de renovar
  // em hepato.app.br/sncr, que abre noutra aba).
  const [saldosSncr, setSaldosSncr] = useState<
    Partial<Record<TipoNumeracao, { saldo: number; total: number }>> | null
  >(null);
  useEffect(() => {
    const uid = usuarioLogado?.id;
    if (!uid || !ehMedico(usuarioLogado)) return;
    let vivo = true;
    const ler = async () => {
      const novos: Partial<Record<TipoNumeracao, { saldo: number; total: number }>> = {};
      for (const t of ["RCE", "RET"] as TipoNumeracao[]) {
        try {
          const c = await lerCofre(uid, ambienteSncr(), t);
          novos[t] = { saldo: c.saldo, total: c.total };
        } catch {
          /* sem leitura: a checagem antes de assinar continua valendo */
        }
      }
      if (vivo) setSaldosSncr(novos);
    };
    ler();
    window.addEventListener("focus", ler);
    return () => {
      vivo = false;
      window.removeEventListener("focus", ler);
    };
  }, [usuarioLogado]);
  // Aviso de estoque baixo: só quando a numeração vale nas assinaturas.
  const saldosBaixosSncr = numeracaoNasAssinaturas(usuarioLogado?.id)
    ? (["RCE", "RET"] as TipoNumeracao[])
        .filter((t) => {
          const c = saldosSncr?.[t];
          return !!c && c.total > 0 && c.saldo < LIMIAR_SALDO_BAIXO;
        })
        .map((t) => ({ tipo: t, saldo: saldosSncr![t]!.saldo }))
    : [];
  const [nomeAmbulatorio, setNomeAmbulatorio] = useState(
    "Ambulatório de Transplante Hepático de Rondônia"
  );
  const [endereco, setEndereco] = useState("Av. Gov. Jorge Teixeira, 3766");
  const [cidade, setCidade] = useState("Porto Velho - RO");
  const [telefone, setTelefone] = useState("(69) 3216-5700");
  // AI_154 — configuração do documento salva por usuário (usuarios/{uid}).
  const { marcarEdicao: marcarEdicaoConfig } = useConfigDocumentoSalva(
    usuarioLogado?.id,
    { nomeAmbulatorio, endereco, cidade, telefone },
    { setNomeAmbulatorio, setEndereco, setCidade, setTelefone },
    !!dadosIniciais?.config
  );
  const [controleEspecial, setControleEspecial] = useState(false);
  const [controleAutoDetectado, setControleAutoDetectado] = useState(false);
  // B356 (AI_154) — D2: respostas do médico às perguntas dos adendos (dose ≤ 100
  // mg? liberação controlada ≤ 40 mg? adesivo?). Guardadas por medicamento E pelo
  // nome: se o médico troca o texto do medicamento, a resposta antiga não vale.
  const [respostasAdendo, setRespostasAdendo] = useState<
    Record<string, { nome: string; respostas: Record<string, boolean> }>
  >({});
  const [substanciasDetectadas, setSubstanciasDetectadas] = useState<string[]>(
    []
  );
  // B306 (AI_140) — Receita de Antimicrobiano (RDC 471/2021). Auto-detectada por
  // princípio ativo; o médico pode desligar (falso positivo). Quando ligada, o
  // antibiótico sai numa receita própria de 2 vias; se houver TAMBÉM controlado,
  // saem DOIS documentos (decisão de Leonardo).
  const [modoAntimicrobiano, setModoAntimicrobiano] = useState(false);
  const [antimicrobianoAutoDetectado, setAntimicrobianoAutoDetectado] =
    useState(false);
  const [antimicrobianosDetectados, setAntimicrobianosDetectados] = useState<
    string[]
  >([]);
  // B350 (AI_151) — mesma mecânica para os análogos de GLP-1 (RDC 973/2025):
  // liga sozinho ao detectar a substância OU a marca, e o médico pode desligar.
  // O documento é o MESMO do antimicrobiano, com validade de 90 dias.
  const [modoGLP1, setModoGLP1] = useState(false);
  const [glp1AutoDetectado, setGlp1AutoDetectado] = useState(false);
  const [glp1Detectados, setGlp1Detectados] = useState<string[]>([]);
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [sugestoesMed, setSugestoesMed] = useState<
    {
      id: string;
      sugestoes: (MedicamentoCatalogo & { apresentacao?: string })[];
    }[]
  >([]);
  const [medicamentosMemoria, setMedicamentosMemoria] = useState<string[]>([]);

  // ============================================
  // 🏥 LME — Componente Especializado (B211, AI_97)
  // ============================================
  // O LME é disparado por MEDICAMENTO (imunossupressores/AUDC). Quando há um
  // desses na receita, oferecemos preencher o laudo, que sai JUNTO com a receita
  // no mesmo documento. dadosLME = null enquanto não preenchido.
  const [dadosLME, setDadosLME] = useState<DadosLME | null>(null);
  const [lmeModalAberto, setLmeModalAberto] = useState(false);

  // ============================================
  // 🏥 HEPATITE B — Solicitação de Medicamentos (B211, AI_97)
  // ============================================
  // Também disparada por MEDICAMENTO (antivirais do HBV); ficha PRÓPRIA (não é o
  // LME). Sai JUNTO com a receita no mesmo documento. dadosHBV = null enquanto
  // não preenchido.
  const { listarSorologiasPorPaciente } = useSorologias();
  const [dadosHBV, setDadosHBV] = useState<DadosHepatiteB | null>(null);
  const [hbvModalAberto, setHbvModalAberto] = useState(false);

  // B212 F4 (AI_126): dados do modal de assinatura digital (null = fechado).
  const [modalAssinatura, setModalAssinatura] =
    useState<ConfigModalAssinatura | null>(null);
  // B306 (AI_140): fila de documentos a assinar em sequência (caso misto =
  // controlado + antimicrobiano juntos → 2 documentos). Ao fechar o modal do
  // documento atual, o próximo da fila é aberto.
  const [filaAssinatura, setFilaAssinatura] = useState<ConfigModalAssinatura[]>(
    []
  );

  // Data de hoje em dd/mm/aaaa (mesma convenção do PedidoExames).
  const dataHojeBR = (): string => {
    const d = new Date();
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    return `${dia}/${mes}/${d.getFullYear()}`;
  };

  // Medicamentos da receita (com nome) que exigem LME — pela base do catálogo.
  const medsLME = medicamentos.filter(
    (m) => m.nome.trim() && !!medicamentoExigeLME(m.nome)
  );

  // Monta o solicitante com dados FRESCOS do Firestore (usuarioLogado é foto do
  // login — ver memória usuario-logado-foto-do-login). O LME usa o CNS do
  // profissional (campo 15), como a GAL.
  const obterSolicitanteLME = async (): Promise<SolicitanteLME> => {
    let ds = usuarioLogado?.dadosSolicitante;
    try {
      if (usuarioLogado) {
        const snap = await getDoc(doc(db, "usuarios", usuarioLogado.id));
        if (snap.exists()) {
          ds = (snap.data() as any).dadosSolicitante || ds;
        }
      }
    } catch (e) {
      // sem rede/permissão: usa os dados da sessão mesmo
    }
    return {
      nomeProfissional: usuarioLogado?.nome || "",
      registroConselho: usuarioLogado?.registroProfissional
        ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
        : "",
      cnsProfissional: ds?.cnsProfissional,
      cpfProfissional: ds?.cpfProfissional,
      unidadeSaude: ds?.unidadeSaude,
      cnes: ds?.cnes,
      municipioAtendimento: ds?.municipioAtendimento,
      codigoIBGE: ds?.codigoIBGE,
      ufAtendimento: ds?.ufAtendimento,
    };
  };

  // Abre o modal do LME, semeando os campos a partir do prontuário (editáveis)
  // e dos medicamentos do CEAF detectados na receita.
  const abrirModalLME = () => {
    setDadosLME((prev) => {
      // Reaproveita o que já foi preenchido; só atualiza a lista de medicamentos
      // do CEAF para refletir a receita atual (preservando quantidades digitadas).
      if (prev) {
        const medsAtuais = medsLME.map((m) => {
          const ja = prev.medicamentos.find((x) => x.nome === m.nome);
          return {
            nome: m.nome,
            quantidades: ja?.quantidades || ["", "", "", "", "", ""],
          };
        });
        return { ...prev, medicamentos: medsAtuais };
      }
      return {
        nomeMae: paciente.nomeMae || "",
        pesoPaciente: paciente.peso ? String(paciente.peso) : "",
        alturaPaciente: paciente.altura ? String(paciente.altura) : "",
        racaCor: "",
        etnia: "",
        telefonePaciente: telefoneVigente(paciente),
        emailPaciente: paciente.email || "",
        docTipo: paciente.cpf ? "CPF" : "CNS",
        cid10: "",
        diagnostico: "",
        anamnese: "",
        tratamentoPrevio: "nao",
        tratamentoPrevioRelato: "",
        incapaz: "nao",
        nomeResponsavel: "",
        preenchidoPor: "Médico solicitante",
        dataSolicitacao: dataHojeBR(),
        medicamentos: medsLME.map((m) => ({
          nome: m.nome,
          quantidades: ["", "", "", "", "", ""],
        })),
      };
    });
    setLmeModalAberto(true);
  };

  const atualizarLME = (campo: keyof DadosLME, valor: any) => {
    setDadosLME((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  };

  // Atualiza a quantidade de um medicamento (linha) num mês (coluna 0..5).
  const atualizarQtdLME = (idxMed: number, idxMes: number, valor: string) => {
    setDadosLME((prev) => {
      if (!prev) return prev;
      const meds = prev.medicamentos.map((m, i) => {
        if (i !== idxMed) return m;
        const qs = [...m.quantidades];
        qs[idxMes] = valor;
        return { ...m, quantidades: qs };
      });
      return { ...prev, medicamentos: meds };
    });
  };

  // Repete a quantidade do mês 1 nos 6 meses (atalho — caso mais comum).
  const repetirMes1LME = (idxMed: number) => {
    setDadosLME((prev) => {
      if (!prev) return prev;
      const meds = prev.medicamentos.map((m, i) => {
        if (i !== idxMed) return m;
        const v = m.quantidades[0] || "";
        return { ...m, quantidades: [v, v, v, v, v, v] };
      });
      return { ...prev, medicamentos: meds };
    });
  };

  // -------------------------------------------------------
  // 🏥 HEPATITE B — detecção + abertura/edição do modal
  // -------------------------------------------------------
  // Medicamentos da receita que disparam a ficha de Hepatite B, mapeados ao id
  // oficial do campo 34 (dedup — TDF e TAF são itens distintos).
  const medsHBVIds: string[] = Array.from(
    new Set(
      medicamentos
        .map((m) => (m.nome.trim() ? medicamentoExigeHepatiteB(m.nome) : undefined))
        .filter((x): x is string => !!x)
    )
  );

  const obterSolicitanteHBV = async (): Promise<SolicitanteHepatiteB> =>
    obterSolicitanteLME(); // mesmos campos do cadastro (CNS/CNES/unidade)

  // Abre o modal da ficha de Hepatite B, semeando do prontuário e das sorologias
  // mais recentes (HBV-DNA, HBeAg, anti-HDV IgG, HDV-RNA, HBsAg/anti-HBc).
  const abrirModalHBV = () => {
    setDadosHBV((prev) => {
      if (prev) {
        // Reabrindo: preserva o que já foi preenchido e garante que os
        // medicamentos detectados na receita atual estejam marcados (campo 34),
        // sem apagar marcações manuais que o médico tenha feito.
        const medicamentos = Array.from(new Set([...prev.medicamentos, ...medsHBVIds]));
        return { ...prev, medicamentos };
      }
      const sorologias = listarSorologiasPorPaciente(paciente.id);
      const s = sorologias[0]; // mais recente (lista ordenada desc)
      return {
        sinan: "",
        // Campo 4 (Prontuário): sem preenchimento automático — fica em branco
        // (decisão de Leonardo, AI_97; o RGCT não corresponde a este campo).
        prontuario: "",
        nomeSocial: "",
        inicioTratamento: "",
        validadePor: "180 dias",
        origemAcompanhamento: "Público",
        pesoPaciente: paciente.peso ? String(paciente.peso) : "",
        cid10: "",
        semCoinfeccao: false,
        coinfeccoes: [],
        hbeag: interpretarReagente(s?.hbeag),
        alt: "",
        hbvDnaResultado: s?.pcrVHB || "",
        hbvDnaData: s?.pcrVHB ? s?.dataColeta || "" : "",
        gravidadeCirrose: "",
        antiHDV: interpretarReagente(s?.antiHDV),
        hdvRna: interpretarDetectavel(s?.pcrVHD),
        betaHCG: paciente.sexo === "M" ? "Negativo" : "",
        diagMaternoData: "",
        diagMaternoIG: "",
        indicacaoTerapeutica: "",
        situacaoProfilaxia: "",
        doadorHBsAg: "",
        doadorAntiHBc: "",
        receptorHBsAg: interpretarReagente(s?.hbsag),
        receptorAntiHBc: interpretarReagente(s?.antiHBcIgG),
        riscoReativacao: "",
        momentoProfilaxia: "",
        riscoReinfeccao: "",
        terapiaPreemptiva: "",
        outrosCriterios: [],
        condicoes: [],
        contraindicacao: [],
        resistenciaFalha: [],
        usoPrevio: [],
        medicamentos: [...medsHBVIds],
        entecavirComprimidos: "",
        observacao: "",
        dataSolicitacao: dataHojeBR(),
      };
    });
    setHbvModalAberto(true);
  };

  const atualizarHBV = (campo: keyof DadosHepatiteB, valor: any) => {
    setDadosHBV((prev) => (prev ? { ...prev, [campo]: valor } : prev));
  };

  // Liga/desliga uma opção num campo de múltipla escolha (campos 12/29/30/31/32/33/34).
  const alternarHBV = (campo: keyof DadosHepatiteB, opcao: string) => {
    setDadosHBV((prev) => {
      if (!prev) return prev;
      const atual = (prev[campo] as string[]) || [];
      const novo = atual.includes(opcao)
        ? atual.filter((x) => x !== opcao)
        : [...atual, opcao];
      return { ...prev, [campo]: novo };
    });
  };

  // Carregar medicamentos já usados do Firebase (memória)
  useEffect(() => {
    if (!usuarioLogado) return;
    const carregarMemoria = async () => {
      try {
        const q = query(
          collection(db, "medicamentosUsados"),
          where("usuarioId", "==", usuarioLogado.id)
        );
        const snapshot = await getDocs(q);
        const nomes: string[] = [];
        snapshot.docs.forEach((d) => {
          const nome = d.data().nome;
          if (nome && !nomes.includes(nome)) nomes.push(nome);
        });
        setMedicamentosMemoria(nomes);
      } catch (error) {
        console.error("Erro ao carregar memória de medicamentos:", error);
      }
    };
    carregarMemoria();
  }, [usuarioLogado]);

  // B356 (AI_154) — classificação de cada medicamento pela Portaria 344/98
  // (RDC 1.036/2026): lista, documento exigido e pergunta pendente (D2).
  const classificacoes = useMemo(
    () =>
      medicamentos
        .filter((m) => m.nome.trim().length >= 3)
        .map((m) => {
          const r = respostasAdendo[m.id];
          const respostas = r && r.nome === m.nome ? r.respostas : {};
          return { med: m, c: classificarMedicamento(m.nome, respostas) };
        })
        .filter(
          (x): x is { med: Medicamento; c: ClassificacaoControlado } =>
            x.c !== null
        ),
    [medicamentos, respostasAdendo]
  );
  // D1 (revista por Leonardo, AI_154, como a Memed): o que exige Notificação
  // (A/B/B2/Especial) NÃO é bloqueado — sai na receita simples com AVISO de que
  // a Notificação (talonário, numeração da Visa) deve ser preenchida à mão e
  // entregue junto (Portaria 344/98: a Notificação acompanha a receita).
  const controladosTalonario = classificacoes.filter(
    (x) => !x.c.pergunta && exigeTalonario(x.c.documento)
  );
  // D2: dose/forma não informadas — o médico responde antes de gerar.
  const controladosPergunta = classificacoes.filter((x) => x.c.pergunta);

  const responderAdendo = (med: Medicamento, chave: string, valor: boolean) =>
    setRespostasAdendo((prev) => ({
      ...prev,
      [med.id]: {
        nome: med.nome,
        respostas: {
          ...(prev[med.id]?.nome === med.nome ? prev[med.id].respostas : {}),
          [chave]: valor,
        },
      },
    }));

  // Detecção automática de substâncias controladas — só as que o app EMITE
  // (Receita de Controle Especial). Notificação A/B sai na receita simples com aviso (D1 revista).
  useEffect(() => {
    const detectados: string[] = [];
    for (const { c } of classificacoes) {
      if (
        c.documento === "controleEspecial" &&
        !c.pergunta &&
        !detectados.includes(c.substancia)
      ) {
        detectados.push(c.substancia);
      }
    }
    // Só atualizar estado se o resultado realmente mudou (evita re-render que rouba foco)
    setSubstanciasDetectadas((prev) => {
      const prevNomes = prev.join(",");
      const novosNomes = detectados.join(",");
      if (prevNomes === novosNomes) return prev; // mesma referência = sem re-render
      return detectados;
    });
    if (detectados.length > 0 && !controleEspecial) {
      setControleEspecial(true);
      setControleAutoDetectado(true);
    } else if (detectados.length === 0 && controleAutoDetectado) {
      setControleEspecial(false);
      setControleAutoDetectado(false);
    }
  }, [classificacoes]);

  // B306 (AI_140) — Detecção automática de ANTIMICROBIANO (RDC 471/2021). Mesma
  // mecânica do controle especial: liga sozinho quando há antibiótico; o médico
  // pode desligar (falso positivo) e não religa até trocar os medicamentos.
  useEffect(() => {
    const medsComNome = medicamentos.filter((m) => m.nome.trim().length >= 3);
    const antim = detectarAntimicrobianos(medsComNome);
    setAntimicrobianosDetectados((prev) => {
      if (prev.join(",") === antim.join(",")) return prev;
      return antim;
    });
    if (antim.length > 0 && !modoAntimicrobiano) {
      setModoAntimicrobiano(true);
      setAntimicrobianoAutoDetectado(true);
    } else if (antim.length === 0 && antimicrobianoAutoDetectado) {
      setModoAntimicrobiano(false);
      setAntimicrobianoAutoDetectado(false);
    }
  }, [medicamentos]);

  // B350 (AI_151) — Detecção automática de ANÁLOGO DE GLP-1 (RDC 973/2025).
  // Espelha a do antimicrobiano de propósito: mesma mecânica, mesma cara para o
  // médico. O que muda é o documento resultante (validade de 90 dias).
  useEffect(() => {
    const medsComNome = medicamentos.filter((m) => m.nome.trim().length >= 3);
    const glp1 = detectarAnalogosGLP1(medsComNome);
    setGlp1Detectados((prev) => {
      if (prev.join(",") === glp1.join(",")) return prev;
      return glp1;
    });
    if (glp1.length > 0 && !modoGLP1) {
      setModoGLP1(true);
      setGlp1AutoDetectado(true);
    } else if (glp1.length === 0 && glp1AutoDetectado) {
      setModoGLP1(false);
      setGlp1AutoDetectado(false);
    }
  }, [medicamentos]);
  // Carregar dados do histórico
  useEffect(() => {
    if (dadosIniciais?.conteudo) {
      const meds =
        dadosIniciais.conteudo.medicamentos || dadosIniciais.conteudo;
      if (Array.isArray(meds)) {
        setMedicamentos(
          meds.map((m: any, i: number) => ({
            id: Date.now().toString() + i,
            nome: m.nome || "",
            posologia: (m.posologia || "").split("\n")[0],
            tipoUso: (m.posologia || "").includes("Uso contínuo")
              ? ("continuo" as const)
              : ("quantidade" as const),
            quantidade: (m.posologia || "").includes("Uso contínuo")
              ? ""
              : (m.posologia || "").split("\n").slice(1).join("").trim(),
          }))
        );
      }
      if (dadosIniciais.config?.nomeAmbulatorio)
        setNomeAmbulatorio(dadosIniciais.config.nomeAmbulatorio);
      if (dadosIniciais.config?.endereco)
        setEndereco(dadosIniciais.config.endereco);
      if (dadosIniciais.config?.cidade) setCidade(dadosIniciais.config.cidade);
      if (dadosIniciais.config?.telefone)
        setTelefone(dadosIniciais.config.telefone);
      if (dadosIniciais.config?.controleEspecial)
        setControleEspecial(dadosIniciais.config.controleEspecial);
      // 🏥 Restaurar formulários auxiliares salvos junto à receita
      const aux = dadosIniciais.conteudo?.formulariosAuxiliares;
      const lme = Array.isArray(aux)
        ? aux.find((a: any) => a?.tipo === "lme")
        : null;
      if (lme?.dados) setDadosLME(lme.dados as DadosLME);
      const hbv = Array.isArray(aux)
        ? aux.find((a: any) => a?.tipo === "hepatiteB")
        : null;
      if (hbv?.dados) setDadosHBV(hbv.dados as DadosHepatiteB);
    }
  }, [dadosIniciais]);

  // Buscar sugestões (catálogo + memória)
  const buscarSugestoes = (medId: string, termo: string) => {
    if (termo.length < 2) {
      setSugestoesMed((prev) => prev.filter((s) => s.id !== medId));
      return;
    }
    const termoNorm = termo
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    // Buscar no catálogo (com apresentações expandidas)
    const doCatalogo = buscarMedicamentos(termo).flatMap((m) =>
      m.apresentacoes.map((ap) => ({ ...m, apresentacao: ap }))
    );

    // Buscar na memória (medicamentos já prescritos)
    const daMemoria = medicamentosMemoria
      .filter((n) =>
        n
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(termoNorm)
      )
      .filter(
        (n) => !doCatalogo.some((c) => `${c.nome} ${c.apresentacao}` === n)
      )
      .map(
        (n) =>
          ({
            nome: n,
            apresentacoes: [],
            categoria: "Recente",
            apresentacao: undefined,
          } as MedicamentoCatalogo & { apresentacao?: string })
      );

    const todas = [...doCatalogo, ...daMemoria].slice(0, 10);
    setSugestoesMed((prev) => {
      const semEste = prev.filter((s) => s.id !== medId);
      return todas.length > 0
        ? [...semEste, { id: medId, sugestoes: todas }]
        : semEste;
    });
  };

  // Salvar medicamento na memória do Firebase
  const salvarNaMemoria = async (nomeMed: string) => {
    if (!usuarioLogado || !nomeMed.trim()) return;
    if (medicamentosMemoria.includes(nomeMed.trim())) return;
    try {
      await addDoc(collection(db, "medicamentosUsados"), {
        nome: nomeMed.trim(),
        usuarioId: usuarioLogado.id,
        criadoEm: serverTimestamp(),
      });
      setMedicamentosMemoria((prev) => [...prev, nomeMed.trim()]);
    } catch (error) {
      console.error("Erro ao salvar na memória:", error);
    }
  };

  const adicionarMedicamento = () => {
    const novoId = Date.now().toString();
    setMedicamentos([
      ...medicamentos,
      {
        id: novoId,
        nome: "",
        posologia: "",
        tipoUso: "continuo",
      },
    ]);
    // Focar automaticamente no input do novo medicamento
    // Delay maior para garantir que useEffects completem antes do foco
    setTimeout(() => {
      const input = document.querySelector(
        `input[data-med-id="${novoId}"]`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        // Segundo foco como fallback caso um re-render intermediário roube
        setTimeout(() => input.focus(), 50);
      }
    }, 150);
  };

  const atualizarMedicamento = (
    id: string,
    campo: "nome" | "posologia" | "tipoUso" | "quantidade",
    valor: string
  ) => {
    setMedicamentos((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [campo]: valor } : m))
    );
  };
  const removerMedicamento = (id: string) => {
    setMedicamentos((prev) => prev.filter((m) => m.id !== id));
  };

  // B212 F4 (AI_126): reúne validação + config + meds + anexos (LME/HBV) + o
  // payload do histórico. Partilhado pela impressão (handleGerarPDF) e pela
  // assinatura digital (handleAssinarReceituario), para que o que se assina seja
  // EXATAMENTE o que se imprime/salva. Retorna null se não houver medicamento
  // válido ou se o usuário cancelar (LME com dados de cadastro faltando).
  const prepararReceituario = async (
    paraAssinatura: boolean
  ): Promise<{
    configPDF: {
      nomeAmbulatorio: string;
      nomeMedico: string;
      crm: string;
      endereco: string;
      cidade: string;
      telefone: string;
    };
    // B306 (AI_140): 1 ou 2 documentos a emitir. Simples/controle/antimicrobiano;
    // 2 documentos só no caso misto (controlado + antimicrobiano juntos). Cada
    // documento carrega seu PRÓPRIO selo/QR + código de validação (não colidem).
    documentos: {
      tipoDoc: "simples" | "controle" | "antimicrobiano" | "glp1" | "rceEletronica";
      // B356 (AI_154) — tipo de numeração do SNCR que o documento consome ao
      // ASSINAR (RCE/RET) e o número atribuído (preenchido na assinatura).
      tipoNumeracao?: TipoNumeracao;
      numeroSncr?: string;
      tipo: string;
      titulo: string;
      meds: { nome: string; posologia: string }[];
      anexos: { estilos: string; corpo: string }[];
      payload: Record<string, unknown>;
      selo?: string;
      seloCompacto?: string;
      seloCompactoSemQR?: string;
      codigoValidacao?: string;
    }[];
  } | null> => {
    const medValidos = medicamentos.filter(
      (m) => m.nome.trim() && m.posologia.trim()
    );
    if (medValidos.length === 0) {
      alert("Adicione pelo menos 1 medicamento com nome e posologia.");
      return null;
    }
    // B356 (AI_154) — D2: não gera com pergunta de dose/forma sem resposta.
    // D1 (revista): controlado que exige Notificação só pede confirmação.
    if (controladosPergunta.length > 0) {
      alert(
        "Responda às perguntas sobre os medicamentos controlados (quadro vermelho acima da lista) antes de gerar a receita."
      );
      return null;
    }
    if (controladosTalonario.length > 0) {
      const seguir = window.confirm(
        "Medicamento que exige Notificação de Receita:\n\n" +
          controladosTalonario
            .map((x) => `• ${x.med.nome} → ${NOME_DOCUMENTO[x.c.documento]}`)
            .join("\n") +
          "\n\nA Notificação deve ser preenchida à mão no talonário (numeração da Vigilância Sanitária) e entregue ao paciente JUNTO com esta receita.\n\nGerar a receita?"
      );
      if (!seguir) return null;
    }
    const configPDF = {
      nomeAmbulatorio,
      nomeMedico: usuarioLogado?.nome || "Médico",
      crm: usuarioLogado?.registroProfissional
        ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
        : "",
      endereco,
      cidade,
      telefone,
    };
    const medFormatados = medValidos.map((m) => ({
      nome: m.nome,
      posologia:
        m.posologia +
        (m.tipoUso === "continuo"
          ? "\nUso contínuo"
          : m.quantidade
          ? `\n${m.quantidade}`
          : ""),
    }));

    // B212 F4 (AI_126) — selo visível ICP-Brasil, SÓ quando vai assinar. Tanto a
    // versão CHEIA (rodapé) quanto a COMPACTA (campos de assinatura das vias e
    // das fichas do SUS) carregam o QR de um CÓDIGO DE VALIDAÇÃO — B306 (AI_140):
    // gerado POR DOCUMENTO, pois no caso misto os 2 documentos não podem
    // compartilhar o mesmo código (o registro `validacoes/{codigo}` sobrescreveria).
    //
    // ⚠️ B340 (AI_150): desde que o selo COMPACTO ganhou QR, ele deixou de poder
    // ser compartilhado entre documentos — um selo único levaria a farmácia que
    // escaneia a ficha/via do documento 2 ao documento 1. Por isso ele nasce
    // dentro de `gerarSeloDoc()`, junto do código, e os anexos são materializados
    // por documento (`anexosFabrica`).
    let dadosSelo:
      | { nome: string; registro: string; data: string }
      | undefined;
    if (paraAssinatura) {
      dadosSelo = {
        nome: usuarioLogado?.nome || "",
        registro: configPDF.crm,
        data: dataHoraSeloAgora(),
      };
    }
    // Gera selo cheio + selo compacto + código de validação FRESCOS para um
    // documento (só ao assinar). No print (dadosSelo indefinido) retorna vazio.
    const gerarSeloDoc = (): {
      selo?: string;
      seloCompacto?: string;
      seloCompactoSemQR?: string;
      codigoValidacao?: string;
    } => {
      if (!dadosSelo) return {};
      const codigoValidacao = gerarCodigoValidacao();
      return {
        selo:
          montarSeloAssinaturaDigital(dadosSelo) + montarBlocoQR(codigoValidacao),
        seloCompacto: montarSeloAssinaturaDigital({
          ...dadosSelo,
          compacto: true,
          codigoValidacao,
        }),
        // B340 — variante SEM QR para a receita de ANTIMICROBIANO: as 2 vias dela
        // já trazem o selo completo com QR grande na mesma página (B306-FU), e um
        // segundo QR ao lado do carimbo confundiria quem escaneia no balcão.
        // Decisão de Leonardo (AI_150). As fichas do SUS anexas seguem COM QR.
        seloCompactoSemQR: montarSeloAssinaturaDigital({
          ...dadosSelo,
          compacto: true,
        }),
        codigoValidacao,
      };
    };

    // 🏥 LME (B211): se o laudo foi preenchido e ainda há medicamento do CEAF na
    // receita, anexa o LME no MESMO documento (folha após a receita). Reconcilia
    // a lista de medicamentos do laudo com a receita atual (preserva as
    // quantidades já digitadas).
    // B340 (AI_150) — FÁBRICAS de anexo, não anexos prontos: cada documento
    // materializa os seus com o PRÓPRIO selo compacto (que carrega o QR do
    // código daquele documento). Chamada com `undefined` reproduz exatamente o
    // comportamento da impressão (sem selo).
    type FabricaAnexo = (
      seloCompacto?: string
    ) => { estilos: string; corpo: string };
    const anexosFabrica: FabricaAnexo[] = [];
    let lmeSalvo: DadosLME | null = null;
    if (dadosLME && medsLME.length > 0) {
      const medsReconciliados = medsLME.map((m) => {
        const ja = dadosLME.medicamentos.find((x) => x.nome === m.nome);
        return {
          nome: m.nome,
          quantidades: ja?.quantidades || ["", "", "", "", "", ""],
        };
      });
      const dadosFinal: DadosLME = { ...dadosLME, medicamentos: medsReconciliados };
      const solicitante = await obterSolicitanteLME();
      const faltando: string[] = [];
      if (!solicitante.cnes) faltando.push("CNES da unidade");
      if (!solicitante.cnsProfissional) faltando.push("CNS do profissional");
      if (faltando.length) {
        const seguir = window.confirm(
          `Atenção: estes dados do seu cadastro estão em branco e sairão vazios no LME: ${faltando.join(
            ", "
          )}.\n\nPara preencher uma vez só: Gestão de Usuários → editar seu usuário → 'Dados para formulários do SUS'.\n\nDeseja gerar mesmo assim?`
        );
        if (!seguir) return null;
      }
      anexosFabrica.push((s) =>
        montarFragmentoLME(paciente, solicitante, dadosFinal, s)
      );
      lmeSalvo = dadosFinal;
    }

    // 🏥 HEPATITE B (B211): se a ficha foi preenchida e ainda há antiviral do
    // HBV na receita, anexa a ficha no MESMO documento (folha após a receita).
    let hbvSalvo: DadosHepatiteB | null = null;
    if (dadosHBV && medsHBVIds.length > 0) {
      // Garante que os medicamentos detectados estejam marcados no campo 34.
      const medicamentos34 = Array.from(
        new Set([...dadosHBV.medicamentos, ...medsHBVIds])
      );
      const dadosFinalHBV: DadosHepatiteB = { ...dadosHBV, medicamentos: medicamentos34 };
      const solicitante = await obterSolicitanteHBV();
      anexosFabrica.push((s) =>
        montarFragmentoHepatiteB(paciente, solicitante, dadosFinalHBV, s)
      );
      hbvSalvo = dadosFinalHBV;
    }

    // 🏥 Formulários auxiliares (LME/HBV) salvos junto à receita. Pertencem ao
    // documento que carrega os medicamentos correspondentes (imunossupressor/
    // antiviral do HBV) — nunca à receita de antimicrobiano.
    const formulariosAuxiliares =
      lmeSalvo || hbvSalvo
        ? [
            ...(lmeSalvo
              ? [{ tipo: "lme", formularioId: "lme-ceaf", dados: lmeSalvo }]
              : []),
            ...(hbvSalvo
              ? [{ tipo: "hepatiteB", formularioId: "hep-b", dados: hbvSalvo }]
              : []),
          ]
        : null;

    const configPayload = {
      nomeAmbulatorio,
      nomeMedico: usuarioLogado?.nome || "Médico",
      crm: usuarioLogado?.registroProfissional
        ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
        : "",
      endereco,
      cidade,
      telefone,
      controleEspecial,
    };

    const montarPayload = (
      meds: { nome: string; posologia: string }[],
      tipo: string,
      titulo: string,
      incluirAux: boolean
    ): Record<string, unknown> => ({
      pacienteId: paciente.id,
      tipo,
      titulo,
      conteudo: {
        medicamentos: meds,
        formulariosAuxiliares: incluirAux ? formulariosAuxiliares : null,
      },
      config: configPayload,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioLogado?.id || "",
      criadoPorNome: usuarioLogado?.nome || "",
    });

    // B306 (AI_140) — decide os documentos a emitir. Classifica cada medicamento:
    // antimicrobiano (RDC 471) × o resto (comum/controlado 344/98).
    // B350 (AI_151) — os análogos de GLP-1 saem PRIMEIRO, e todo o resto da
    // lógica passa a operar sobre `base`. Quando não há GLP-1, `base` é
    // exatamente `medFormatados` e o caminho abaixo é idêntico ao de antes.
    const medsGLP1 = medFormatados.filter(
      (m) => detectarAnalogoGLP1(m.nome) !== null
    );
    const temGLP1 = modoGLP1 && medsGLP1.length > 0;
    const base = temGLP1
      ? medFormatados.filter((m) => detectarAnalogoGLP1(m.nome) === null)
      : medFormatados;

    const medsAntim = base.filter(
      (m) => detectarAntimicrobiano(m.nome) !== null
    );
    const medsResto = base.filter(
      (m) => detectarAntimicrobiano(m.nome) === null
    );
    const temAntim = modoAntimicrobiano && medsAntim.length > 0;

    const TITULO_ANTIM = "Receita de Antimicrobiano";
    const TITULO_GLP1 = "Receita com Retenção — Análogo de GLP-1";
    const TITULO_CONTROLE = "Receituário + Controle Especial";
    type DocumentoPlano = {
      tipoDoc: "simples" | "controle" | "antimicrobiano" | "glp1" | "rceEletronica";
      tipoNumeracao?: TipoNumeracao;
      numeroSncr?: string;
      tipo: string;
      titulo: string;
      meds: { nome: string; posologia: string }[];
      anexos: { estilos: string; corpo: string }[];
      payload: Record<string, unknown>;
      selo?: string;
      seloCompacto?: string;
      seloCompactoSemQR?: string;
      codigoValidacao?: string;
    };
    // Cada documento recebe um selo/QR + código de validação PRÓPRIOS (gerarSeloDoc
    // gera frescos), evitando colisão no registro `validacoes/{codigo}`.
    const criarDoc = (
      tipoDoc: DocumentoPlano["tipoDoc"],
      tipo: string,
      titulo: string,
      meds: { nome: string; posologia: string }[],
      fabricas: FabricaAnexo[],
      incluirAux: boolean
    ): DocumentoPlano => {
      const selos = gerarSeloDoc();
      return {
        tipoDoc,
        tipo,
        titulo,
        meds,
        // B340 — os anexos nascem com o selo compacto DESTE documento.
        anexos: fabricas.map((f) => f(selos.seloCompacto)),
        payload: montarPayload(meds, tipo, titulo, incluirAux),
        ...selos,
      };
    };

    const documentos: DocumentoPlano[] = [];

    // B356 Fase 1 (AI_154) — NUMERAÇÃO DO SNCR na ASSINATURA (RDC 1.000/2025).
    // Só vale para documento ASSINADO e quando a numeração está ativa
    // (produção, ou homologação com "modo teste" ligado em /sncr):
    //   - antimicrobiano/GLP-1 (RET): o mesmo documento, marcado para receber N.º;
    //   - "Receituário + Controle Especial": cada medicamento de CONTROLE
    //     ESPECIAL vira uma Receita de Controle Especial ELETRÔNICA própria
    //     (modelo oficial da Anvisa, 1 medicamento, 1 número); os demais
    //     medicamentos ficam num receituário comum. O 1º documento eletrônico
    //     herda o selo/código e os anexos (LME etc.) do documento original —
    //     os anexos foram montados com o selo DAQUELE código.
    const respostasDoNome = (nome: string): Record<string, boolean> => {
      const m = medicamentos.find((x) => x.nome === nome);
      const r = m ? respostasAdendo[m.id] : undefined;
      return r && r.nome === nome ? r.respostas : {};
    };
    const TITULO_RCE_ELETRONICA = "Receita de Controle Especial Eletrônica";
    const aplicarNumeracaoSncr = (docs: DocumentoPlano[]): DocumentoPlano[] => {
      if (!paraAssinatura || !numeracaoNasAssinaturas(usuarioLogado?.id)) return docs;
      const ehCE = (nome: string) =>
        classificarMedicamento(nome, respostasDoNome(nome))?.documento ===
        "controleEspecial";
      const novaRce = (m: { nome: string; posologia: string }): DocumentoPlano => ({
        ...criarDoc(
          "rceEletronica",
          "receituario-controle-eletronica",
          TITULO_RCE_ELETRONICA,
          [m],
          [],
          false
        ),
        tipoNumeracao: "RCE",
      });
      const out: DocumentoPlano[] = [];
      // Revisão adversarial (AI_154): TODO documento é varrido — não só o de
      // "controle". Um C1 num receituário simples (controle desligado à mão) ou
      // no documento de antimicrobiano também exige RCE numerada (Art. 7º).
      for (const d of docs) {
        const ret = d.tipoDoc === "antimicrobiano" || d.tipoDoc === "glp1";
        const ce = d.meds.filter((m) => ehCE(m.nome));
        const resto = d.meds.filter((m) => !ehCE(m.nome));
        if (ce.length === 0) {
          // "controle" ligado à mão sem substância reconhecida fica como está.
          out.push(ret ? { ...d, tipoNumeracao: "RET" } : d);
          continue;
        }
        if (resto.length > 0) {
          // O documento original (selo, código e anexos — LME etc.) fica com os
          // medicamentos restantes; cada controlado vira uma RCE própria.
          const virouSimples = d.tipoDoc === "controle";
          const tipo = virouSimples ? "receituario" : d.tipo;
          const titulo = virouSimples ? "Receituário" : d.titulo;
          out.push({
            ...d,
            tipoDoc: virouSimples ? "simples" : d.tipoDoc,
            tipo,
            titulo,
            meds: resto,
            payload: {
              ...d.payload,
              tipo,
              titulo,
              conteudo: {
                ...(d.payload.conteudo as Record<string, unknown>),
                medicamentos: resto,
              },
            },
            ...(ret ? { tipoNumeracao: "RET" as TipoNumeracao } : {}),
          });
          ce.forEach((m) => out.push(novaRce(m)));
        } else {
          // Só controlados: a 1ª RCE herda o selo/código/anexos do original.
          ce.forEach((m, i) => {
            if (i > 0) {
              out.push(novaRce(m));
              return;
            }
            out.push({
              ...d,
              tipoDoc: "rceEletronica",
              tipo: "receituario-controle-eletronica",
              titulo: TITULO_RCE_ELETRONICA,
              meds: [m],
              payload: {
                ...d.payload,
                tipo: "receituario-controle-eletronica",
                titulo: TITULO_RCE_ELETRONICA,
                conteudo: {
                  ...(d.payload.conteudo as Record<string, unknown>),
                  medicamentos: [m],
                },
              },
              tipoNumeracao: "RCE",
            });
          });
        }
      }
      return out;
    };

    // B350 — GLP-1 sozinho na receita: o documento dele leva TUDO (+ anexos),
    // mesmo padrão que o antimicrobiano já usava nesse caso.
    if (temGLP1 && base.length === 0) {
      documentos.push(
        criarDoc(
          "glp1",
          "receituario-glp1",
          TITULO_GLP1,
          medFormatados,
          anexosFabrica,
          true
        )
      );
      return { configPDF, documentos: aplicarNumeracaoSncr(documentos) };
    }
    // Havendo mais coisas na receita, o GLP-1 vira documento PRÓPRIO — a validade
    // é outra (90 dias), então não pode dividir papel com o resto.
    if (temGLP1) {
      documentos.push(
        criarDoc("glp1", "receituario-glp1", TITULO_GLP1, medsGLP1, [], false)
      );
    }

    if (temAntim && controleEspecial && medsResto.length > 0) {
      // CASO MISTO (decisão de Leonardo): 2 documentos separados. Controle leva os
      // controlados/comuns (+ anexos); antimicrobiano leva só os antibióticos.
      documentos.push(
        criarDoc(
          "controle",
          "receituario-controle",
          TITULO_CONTROLE,
          medsResto,
          anexosFabrica,
          true
        )
      );
      documentos.push(
        criarDoc(
          "antimicrobiano",
          "receituario-antimicrobiano",
          TITULO_ANTIM,
          medsAntim,
          [],
          false
        )
      );
    } else if (temAntim) {
      // Só antimicrobiano (sem controlado): a receita de antimicrobiano leva
      // TODOS os medicamentos (mesmo padrão do controle especial hoje).
      documentos.push(
        criarDoc(
          "antimicrobiano",
          "receituario-antimicrobiano",
          TITULO_ANTIM,
          base,
          anexosFabrica,
          true
        )
      );
    } else if (controleEspecial) {
      documentos.push(
        criarDoc(
          "controle",
          "receituario-controle",
          TITULO_CONTROLE,
          base,
          anexosFabrica,
          true
        )
      );
    } else {
      documentos.push(
        criarDoc("simples", "receituario", "Receituário", base, anexosFabrica, true)
      );
    }

    return {
      configPDF,
      documentos: aplicarNumeracaoSncr(documentos),
    };
  };

  // B306 (AI_140): monta o HTML de impressão de um documento do plano.
  const montarHTMLImpressao = (
    prep: NonNullable<Awaited<ReturnType<typeof prepararReceituario>>>,
    doc: (typeof prep.documentos)[number]
  ) => {
    const anexos = doc.anexos.length ? doc.anexos : undefined;
    if (doc.tipoDoc === "antimicrobiano" || doc.tipoDoc === "glp1") {
      // B350 — mesmo documento, regimes diferentes (10 dias x 90 dias).
      gerarPDFReceitaAntimicrobiano(
        paciente,
        doc.meds,
        prep.configPDF,
        anexos,
        doc.tipoDoc === "glp1" ? REGIME_GLP1 : REGIME_ANTIMICROBIANO
      );
    } else if (doc.tipoDoc === "controle") {
      gerarPDFReceituarioComControle(paciente, doc.meds, prep.configPDF, anexos);
    } else {
      gerarPDFReceituario(paciente, doc.meds, prep.configPDF, anexos);
    }
  };

  const handleGerarPDF = async () => {
    const prep = await prepararReceituario(false);
    if (!prep) return;

    // Abre TODAS as janelas de impressão primeiro (dentro do gesto do clique —
    // abrir após um await faria o navegador bloquear a 2ª no caso misto).
    for (const doc of prep.documentos) {
      montarHTMLImpressao(prep, doc);
    }
    // Depois persiste cada documento no histórico.
    for (const doc of prep.documentos) {
      try {
        await addDoc(collection(db, "prescricoes"), doc.payload);
        if (onPrescricaoSalva) onPrescricaoSalva();
      } catch (error) {
        console.error("Erro ao salvar no histórico:", error);
      }
    }
    // Limpar formulário
    setMedicamentos([]);
    setDadosLME(null);
    setLmeModalAberto(false);
    setDadosHBV(null);
    setHbvModalAberto(false);
  };

  // B212 F4 (AI_126) / B306 (AI_140): monta o HTML ASSINÁVEL de um documento do
  // plano (com selo/QR). É byte-idêntico ao que a impressão gera + o selo.
  const montarHTMLAssinado = (
    prep: NonNullable<Awaited<ReturnType<typeof prepararReceituario>>>,
    doc: (typeof prep.documentos)[number]
  ): string => {
    const anexos = doc.anexos.length ? doc.anexos : undefined;
    const homologacao = ambienteSncr() === "hmg";
    // B356 (AI_154) + AI_155 (padrão Memed): documento com N.º do SNCR = folha
    // PERSONALIZADA (receituário do ambulatório, fica com o paciente) + folha
    // OFICIAL intacta (modelo da Anvisa, a que a farmácia confere/registra).
    const numerado =
      doc.tipoDoc === "rceEletronica" ||
      (!!doc.numeroSncr && (doc.tipoDoc === "antimicrobiano" || doc.tipoDoc === "glp1"));
    if (numerado) {
      const cnpj = cnpjPlataforma();
      const folhaOficial = montarFolhaOficialSncr(paciente, {
        tipo: doc.tipoDoc === "rceEletronica" ? "RCE" : "RET",
        numero: doc.numeroSncr || "",
        medicamentos: doc.tipoDoc === "rceEletronica" ? [doc.meds[0]] : doc.meds,
        emitente: {
          nome: prep.configPDF.nomeMedico,
          inscricao: prep.configPDF.crm,
          // Nome do ambulatório DENTRO do campo "Endereço completo" (decisão de
          // Leonardo, AI_154): o modelo oficial não admite logo/cabeçalho, mas
          // o conteúdo do campo identifica o serviço de origem.
          endereco: [prep.configPDF.nomeAmbulatorio, prep.configPDF.endereco, prep.configPDF.cidade]
            .filter(Boolean)
            .join(" — "),
          telefone: prep.configPDF.telefone,
        },
        identificacaoPlataforma: `hepato.app${cnpj ? ` — CNPJ ${formatarCnpj(cnpj)}` : ""}`,
        assinatura: {
          nome: usuarioLogado?.nome || "",
          registro: prep.configPDF.crm,
          data: dataHoraSeloAgora(),
        },
        urlVerificacao: doc.codigoValidacao ? urlValidacao(doc.codigoValidacao) : "",
        blocoQR: doc.codigoValidacao ? montarBlocoQR(doc.codigoValidacao) : "",
        homologacao,
      });
      if (doc.tipoDoc === "rceEletronica") {
        return montarReceituarioHTML(
          paciente,
          doc.meds,
          prep.configPDF,
          [folhaOficial, ...doc.anexos],
          doc.selo
        );
      }
      // RET: a nossa via mantém o modelo próprio da receita de antimicrobiano
      // (título, emitente, idade/sexo, validade) — pedido de Leonardo, AI_155.
      return montarReceitaAntimicrobianoHTML(
        paciente,
        doc.meds,
        prep.configPDF,
        [folhaOficial, ...doc.anexos],
        doc.selo,
        doc.seloCompactoSemQR,
        doc.tipoDoc === "glp1" ? REGIME_GLP1 : REGIME_ANTIMICROBIANO,
        true
      );
    }
    if (doc.tipoDoc === "antimicrobiano" || doc.tipoDoc === "glp1") {
      return montarReceitaAntimicrobianoHTML(
        paciente,
        doc.meds,
        prep.configPDF,
        anexos,
        doc.selo,
        // B340 — sem QR aqui: as 2 vias já têm o selo completo com QR grande.
        doc.seloCompactoSemQR,
        // B350 — o regime tem de ser o MESMO da impressão, senão o PDF assinado
        // sairia com validade diferente do papel.
        doc.tipoDoc === "glp1" ? REGIME_GLP1 : REGIME_ANTIMICROBIANO
      );
    }
    if (doc.tipoDoc === "controle") {
      return montarReceituarioComControleHTML(
        paciente,
        doc.meds,
        prep.configPDF,
        anexos,
        doc.selo,
        doc.seloCompacto
      );
    }
    return montarReceituarioHTML(
      paciente,
      doc.meds,
      prep.configPDF,
      anexos,
      doc.selo
    );
  };

  // B212 F4 (AI_126): assina digitalmente (médico-only + flag). Monta o MESMO HTML
  // da impressão, salva cada prescrição (captura o id p/ o broker validar a autoria)
  // e abre o ModalAssinatura. No caso misto (2 documentos), o 2º entra na fila e é
  // aberto ao fechar o 1º. NÃO limpa o formulário se a assinatura pode falhar.
  const handleAssinarReceituario = async () => {
    const prep = await prepararReceituario(true);
    if (!prep) return;

    // B356 (AI_154) — confere o SALDO de numeração do SNCR ANTES de salvar
    // qualquer coisa: sem saldo, nada é gravado e o médico é levado a /sncr.
    const amb = ambienteSncr();
    const uid = usuarioLogado?.id || "";
    const precisa: Record<TipoNumeracao, number> = { RCE: 0, RET: 0 };
    prep.documentos.forEach((d) => {
      if (d.tipoNumeracao) precisa[d.tipoNumeracao]++;
    });
    for (const t of ["RCE", "RET"] as TipoNumeracao[]) {
      if (!precisa[t]) continue;
      try {
        const cofre = await lerCofre(uid, amb, t);
        if (cofre.saldo < precisa[t]) {
          alert(
            `Numeração do SNCR insuficiente para ${t === "RCE" ? "Receita de Controle Especial" : "receita com retenção (antimicrobiano/GLP-1)"}: ` +
              `precisa de ${precisa[t]}, saldo ${cofre.saldo}.\n\nUse o botão "Obter numeração (gov.br)" logo abaixo de "Assinar digitalmente". A receita impressa (assinada à caneta) continua disponível em "Gerar Receituário".`
          );
          return;
        }
      } catch (e) {
        console.error("Erro ao ler a numeração do SNCR:", e);
        alert("Não foi possível conferir a numeração do SNCR. Tente novamente.");
        return;
      }
    }

    // B356 — requisitos do MODELO OFICIAL da RCE eletrônica: CPF e endereço do
    // paciente e QUANTIDADE (não "Uso contínuo") — campos obrigatórios do modelo.
    const rces = prep.documentos.filter((d) => d.tipoDoc === "rceEletronica");
    if (rces.length > 0) {
      const faltas: string[] = [];
      if (!paciente.cpf) faltas.push("CPF do paciente (cadastro)");
      if (!paciente.logradouro && !paciente.cidade)
        faltas.push("endereço do paciente (cadastro)");
      rces.forEach((d) => {
        const linhas = (d.meds[0]?.posologia || "").split("\n").slice(1).join(" ").trim();
        if (!linhas || /uso cont[ií]nuo/i.test(linhas))
          faltas.push(`quantidade de "${d.meds[0]?.nome}" (ex.: 60 comprimidos) — marque "Quantidade" em vez de "Uso contínuo"`);
      });
      if (faltas.length > 0) {
        alert(
          "A Receita de Controle Especial eletrônica (modelo oficial da Anvisa) exige:\n\n" +
            faltas.map((f) => `• ${f}`).join("\n")
        );
        return;
      }
    }

    // Revisão adversarial (AI_154): TODOS os números são obtidos ANTES de salvar
    // qualquer prescrição. Se o cofre falhar no meio, nada foi salvo (os números
    // já consumidos ficam registrados em `usos` como gastos — nunca reaproveitados).
    for (const d of prep.documentos) {
      if (!d.tipoNumeracao) continue;
      try {
        d.numeroSncr = await consumirNumero(uid, amb, d.tipoNumeracao, {
          pacienteId: paciente.id,
          documento: d.titulo,
        });
        d.payload = {
          ...d.payload,
          numeroSncr: d.numeroSncr,
          sncrTipo: d.tipoNumeracao,
          sncrAmbiente: amb,
        };
      } catch (e: any) {
        console.error("Erro ao obter número do SNCR:", e);
        alert(
          `Não foi possível obter o número do SNCR: ${e?.message || "erro desconhecido"}. Nada foi salvo.`
        );
        return;
      }
    }

    const configs: ConfigModalAssinatura[] = [];
    for (const d of prep.documentos) {
      let prescricaoId: string;
      try {
        const docRef = await addDoc(collection(db, "prescricoes"), d.payload);
        prescricaoId = docRef.id;
        if (onPrescricaoSalva) onPrescricaoSalva();
      } catch (error) {
        console.error("Erro ao salvar a prescrição para assinatura:", error);
        alert(
          configs.length > 0
            ? `Não foi possível salvar "${d.titulo}". Os documentos anteriores seguem para assinatura; refaça este depois.`
            : "Não foi possível salvar a prescrição. Tente novamente."
        );
        break;
      }
      const html = montarHTMLAssinado(prep, d);
      configs.push({
        html,
        tipo: d.tipo,
        prescricaoId,
        dados: {
          name: usuarioLogado?.nome || "",
          reason: d.titulo,
          location: cidade || "",
        },
        nomeArquivo: `${
          d.tipoDoc === "antimicrobiano"
            ? "antimicrobiano"
            : d.tipoDoc === "glp1"
            ? "glp1"
            : d.tipoDoc === "rceEletronica"
            ? "controle-especial-eletronica"
            : "receituario"
        }-${paciente.nome}`.replace(/\s+/g, "_"),
        codigoValidacao: d.codigoValidacao,
        dadosValidacao: {
          medicoNome: usuarioLogado?.nome || "",
          registro: prep.configPDF.crm,
          pacienteTelefone: telefoneVigente(paciente),
          pacienteNascimento: paciente.dataNascimento || "",
        },
      });
    }

    if (configs.length === 0) return;
    setModalAssinatura(configs[0]);
    setFilaAssinatura(configs.slice(1));

    // As prescrições já foram salvas no histórico — limpa o editor para a próxima
    // (mesmo comportamento do "Gerar Receituário"). O modal usa o `html` montado.
    setMedicamentos([]);
    setDadosLME(null);
    setLmeModalAberto(false);
    setDadosHBV(null);
    setHbvModalAberto(false);
  };

  // B306 (AI_140): ao fechar o modal, avança a fila (caso misto = 2 documentos).
  const handleFecharAssinatura = () => {
    setFilaAssinatura((fila) => {
      if (fila.length > 0) {
        setModalAssinatura(fila[0]);
        return fila.slice(1);
      }
      setModalAssinatura(null);
      return fila;
    });
  };
  const limparTodos = () => {
    if (
      medicamentos.length > 0 &&
      window.confirm("Limpar todos os medicamentos?")
    ) {
      setMedicamentos([]);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* CONFIG */}
      <div
        style={{
          background: theme.gray[50],
          borderRadius: theme.borderRadius.md,
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setMostrarConfig(!mostrarConfig)}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: theme.gray[600],
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Settings size={12} /> Configuração do Documento
          </span>
          {mostrarConfig ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
        {mostrarConfig && (
          <div
            style={{
              marginTop: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Nome do Ambulatório
              </label>
              <input
                type="text"
                value={nomeAmbulatorio}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setNomeAmbulatorio(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Endereço
              </label>
              <input
                type="text"
                value={endereco}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setEndereco(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Cidade e UF
                </label>
                <input
                  type="text"
                  value={cidade}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setCidade(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Telefone
                </label>
                <input
                  type="text"
                  value={telefone}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setTelefone(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* B356 (AI_154) — revisão adversarial: o "modo teste" do SNCR NÃO pode
          ficar ligado em silêncio — toda receita assinada sairia "SEM VALOR
          LEGAL". Aviso visível + botão para desligar (e expira sozinho em 12 h). */}
      {ambienteSncr() === "hmg" && numeracaoNasAssinaturas(usuarioLogado?.id) && (
        <div
          style={{
            padding: "8px 12px",
            background: "#fef2f2",
            border: "2px solid #b91c1c",
            borderRadius: theme.borderRadius.md,
            fontFamily: theme.fonts.sans,
            fontSize: "12px",
            color: "#7f1d1d",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <strong>MODO TESTE SNCR LIGADO</strong> — receitas assinadas digitalmente saem com
          numeração de teste e a tarja "SEM VALOR LEGAL". Use só com paciente de teste.
          <button
            type="button"
            onClick={() => {
              definirModoTeste(false, usuarioLogado?.id);
              setMedicamentos((m) => [...m]);
            }}
            style={{
              padding: "2px 10px",
              fontSize: "12px",
              border: "1px solid #b91c1c",
              background: "white",
              color: "#7f1d1d",
              borderRadius: theme.borderRadius.sm,
              cursor: "pointer",
            }}
          >
            Desligar
          </button>
        </div>
      )}

      {saldosBaixosSncr.length > 0 && (
        <div
          style={{
            padding: "8px 12px",
            background: "#fffbeb",
            border: "1px solid #d97706",
            borderRadius: theme.borderRadius.md,
            fontFamily: theme.fonts.sans,
            fontSize: "12px",
            color: "#78350f",
          }}
        >
          <strong>Numeração do SNCR acabando</strong> —{" "}
          {saldosBaixosSncr
            .map((b) => `${b.tipo === "RCE" ? "controle especial" : "antimicrobiano/GLP-1"}: ${b.saldo}`)
            .join("; ")}{" "}
          restantes.{" "}
          <button
            type="button"
            onClick={() => abrirObtencaoPeloApp(saldosBaixosSncr.map((b) => b.tipo))}
            style={{
              color: "#78350f",
              fontWeight: 600,
              background: "none",
              border: "none",
              padding: 0,
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "12px",
              fontFamily: theme.fonts.sans,
            }}
          >
            Renovar numeração
          </button>
        </div>
      )}

      {/* B356 (AI_154) — D1/D2: controlados que o app não emite (talonário) e
          perguntas de dose/forma dos adendos da Portaria 344/98. */}
      {(controladosTalonario.length > 0 || controladosPergunta.length > 0) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            padding: controladosPergunta.length ? "10px 12px" : 0,
            background: controladosPergunta.length ? "#fef2f2" : "transparent",
            border: controladosPergunta.length ? "1px solid #f87171" : "none",
            borderRadius: theme.borderRadius.md,
            fontFamily: theme.fonts.sans,
            fontSize: "12px",
            color: "#7f1d1d",
          }}
        >
          {controladosTalonario.map(({ med, c }) => (
            <div
              key={`t-${med.id}`}
              style={{
                background: "#fffbeb",
                border: "1px solid #f59e0b",
                borderRadius: theme.borderRadius.sm,
                padding: "6px 8px",
                color: "#78350f",
              }}
            >
              ⚠️ <strong>{med.nome}</strong> — lista {c.lista}: exige{" "}
              <strong>{NOME_DOCUMENTO[c.documento]}</strong>. Preencha a
              Notificação à mão no talonário (numeração da Vigilância Sanitária)
              e entregue ao paciente junto com esta receita.
              <div style={{ fontSize: "10px", color: "#92400e" }}>{c.fundamento}</div>
            </div>
          ))}
          {controladosPergunta.map(({ med, c }) => (
            <div
              key={`p-${med.id}`}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}
            >
              <span>
                ❓ <strong>{med.nome}</strong> — {c.pergunta!.texto}
              </span>
              {[true, false].map((valor) => (
                <button
                  key={String(valor)}
                  type="button"
                  onClick={() => responderAdendo(med, c.pergunta!.chave, valor)}
                  style={{
                    padding: "2px 10px",
                    fontSize: "12px",
                    borderRadius: theme.borderRadius.sm,
                    border: "1px solid #b91c1c",
                    background: "white",
                    color: "#7f1d1d",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {valor ? "Sim" : "Não"}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* B350-FU (AI_151, pedido de Leonardo): os três seletores de tipo de
          documento ficavam em três linhas empilhadas e comiam a altura da
          página. Agora dividem UMA linha, com quebra automática. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "8px",
        }}
      >
      {/* TOGGLE CONTROLE ESPECIAL */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "8px 12px",
          // B350-FU (AI_151) — os 3 seletores dividem uma linha só.
          // O flex-basis de 240px faz quebrarem sozinhos em tela estreita.
          flex: "1 1 240px",
          minWidth: 0,
          background: controleEspecial ? "#fef3c7" : theme.gray[50],
          borderRadius: theme.borderRadius.md,
          border: controleEspecial
            ? "1px solid #f59e0b"
            : `1px solid ${theme.gray[200]}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            if (substanciasDetectadas.length > 0 && controleEspecial) {
              if (
                window.confirm(
                  "Há medicamentos controlados na prescrição. Deseja desativar o controle especial mesmo assim?"
                )
              ) {
                setControleEspecial(false);
                setControleAutoDetectado(false);
              }
            } else {
              setControleEspecial(!controleEspecial);
              setControleAutoDetectado(false);
            }
          }}
        >
          <input
            type="checkbox"
            checked={controleEspecial}
            readOnly
            style={{ cursor: "pointer" }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: controleEspecial ? "#92400e" : theme.gray[600],
              fontFamily: theme.fonts.sans,
            }}
          >
            Incluir Receita de Controle Especial (Portaria 344/98)
          </span>
          {controleEspecial && (
            <span
              style={{
                fontSize: "10px",
                color: "#b45309",
                fontFamily: theme.fonts.sans,
                marginLeft: "auto",
              }}
            >
              2 vias adicionais no PDF
            </span>
          )}
        </div>
        {substanciasDetectadas.length > 0 && (
          <div
            style={{
              fontSize: "11px",
              color: "#b45309",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "24px",
            }}
          >
            ⚠️ Detectado: {substanciasDetectadas.join(", ")} — controle especial
            ativado automaticamente
          </div>
        )}
      </div>

      {/* B306 (AI_140) — TOGGLE RECEITA DE ANTIMICROBIANO (RDC 471/2021) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "8px 12px",
          // B350-FU (AI_151) — os 3 seletores dividem uma linha só.
          // O flex-basis de 240px faz quebrarem sozinhos em tela estreita.
          flex: "1 1 240px",
          minWidth: 0,
          background: modoAntimicrobiano ? "#e0f2fe" : theme.gray[50],
          borderRadius: theme.borderRadius.md,
          border: modoAntimicrobiano
            ? "1px solid #0284c7"
            : `1px solid ${theme.gray[200]}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            if (antimicrobianosDetectados.length > 0 && modoAntimicrobiano) {
              if (
                window.confirm(
                  "Há antimicrobiano na prescrição (RDC 471/2021 exige receita de 2 vias). Deseja desativar mesmo assim?"
                )
              ) {
                setModoAntimicrobiano(false);
                setAntimicrobianoAutoDetectado(false);
              }
            } else {
              setModoAntimicrobiano(!modoAntimicrobiano);
              setAntimicrobianoAutoDetectado(false);
            }
          }}
        >
          <input
            type="checkbox"
            checked={modoAntimicrobiano}
            readOnly
            style={{ cursor: "pointer" }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: modoAntimicrobiano ? "#075985" : theme.gray[600],
              fontFamily: theme.fonts.sans,
            }}
          >
            Receita de Antimicrobiano (RDC 471/2021)
          </span>
          {modoAntimicrobiano && (
            <span
              style={{
                fontSize: "10px",
                color: "#0369a1",
                fontFamily: theme.fonts.sans,
                marginLeft: "auto",
              }}
            >
              2 vias · validade 10 dias
            </span>
          )}
        </div>
        {antimicrobianosDetectados.length > 0 && (
          <div
            style={{
              fontSize: "11px",
              color: "#0369a1",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "24px",
            }}
          >
            💊 Detectado: {antimicrobianosDetectados.join(", ")} — receita de
            antimicrobiano ativada automaticamente
          </div>
        )}
        {modoAntimicrobiano && controleEspecial && (
          <div
            style={{
              fontSize: "11px",
              color: "#075985",
              fontFamily: theme.fonts.sans,
              paddingLeft: "24px",
            }}
          >
            ⚠️ Há controlado E antimicrobiano — serão gerados 2 documentos
            separados (Controle Especial + Receita de Antimicrobiano).
          </div>
        )}
      </div>

      {/* B350 (AI_151) — TOGGLE RECEITA COM RETENÇÃO / GLP-1 (RDC 973/2025) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "8px 12px",
          // B350-FU (AI_151) — os 3 seletores dividem uma linha só.
          // O flex-basis de 240px faz quebrarem sozinhos em tela estreita.
          flex: "1 1 240px",
          minWidth: 0,
          background: modoGLP1 ? "#f3e8ff" : theme.gray[50],
          borderRadius: theme.borderRadius.md,
          border: modoGLP1
            ? "1px solid #9333ea"
            : `1px solid ${theme.gray[200]}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            if (glp1Detectados.length > 0 && modoGLP1) {
              if (
                window.confirm(
                  "Há análogo de GLP-1 na prescrição (a RDC 973/2025 exige receita de 2 vias com retenção). Deseja desativar mesmo assim?"
                )
              ) {
                setModoGLP1(false);
                setGlp1AutoDetectado(false);
              }
            } else {
              setModoGLP1(!modoGLP1);
              setGlp1AutoDetectado(false);
            }
          }}
        >
          <input
            type="checkbox"
            checked={modoGLP1}
            readOnly
            style={{ cursor: "pointer" }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: modoGLP1 ? "#6b21a8" : theme.gray[600],
              fontFamily: theme.fonts.sans,
            }}
          >
            Receita com retenção — análogo de GLP-1 (RDC 973/2025)
          </span>
          {modoGLP1 && (
            <span
              style={{
                fontSize: "10px",
                color: "#7e22ce",
                fontFamily: theme.fonts.sans,
                marginLeft: "auto",
              }}
            >
              2 vias · validade 90 dias
            </span>
          )}
        </div>
        {glp1Detectados.length > 0 && (
          <div
            style={{
              fontSize: "11px",
              color: "#7e22ce",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "24px",
            }}
          >
            💉 Detectado: {glp1Detectados.join(", ")} — receita com retenção
            ativada automaticamente
          </div>
        )}
        {modoGLP1 && (controleEspecial || modoAntimicrobiano) && (
          <div
            style={{
              fontSize: "11px",
              color: "#6b21a8",
              fontFamily: theme.fonts.sans,
              paddingLeft: "24px",
            }}
          >
            ⚠️ O análogo de GLP-1 sai em documento PRÓPRIO — a validade dele é de
            90 dias, diferente dos demais.
          </div>
        )}
      </div>
      </div>

      {/* LISTA DE MEDICAMENTOS */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: theme.gray[700],
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Pill size={14} /> Medicamentos
          </span>
          <div style={{ display: "flex", gap: "6px" }}>
            {medicamentos.length > 0 && (
              <button
                onClick={limparTodos}
                style={{
                  padding: "4px 8px",
                  background: "#fee2e2",
                  color: "#dc2626",
                  border: "none",
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "11px",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Trash2 size={12} /> Limpar
              </button>
            )}
            <button
              onClick={adicionarMedicamento}
              style={{
                padding: "4px 10px",
                background: theme.primary[600],
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.sm,
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Plus size={12} /> Adicionar
            </button>
          </div>
        </div>

        {medicamentos.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "32px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
              color: theme.gray[400],
              fontSize: "13px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Pill
              size={32}
              color={theme.gray[300]}
              style={{ marginBottom: "8px" }}
            />
            <p>Clique em "Adicionar" para incluir medicamentos</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {medicamentos.map((med, idx) => (
              <div
                key={med.id}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  padding: "10px",
                  background: idx % 2 === 0 ? "white" : theme.gray[50],
                  border: `1px solid ${theme.gray[200]}`,
                  borderRadius: theme.borderRadius.md,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "6px",
                    color: theme.gray[400],
                  }}
                >
                  <GripVertical size={14} />
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "13px",
                      minWidth: "20px",
                    }}
                  >
                    {idx + 1}.
                  </span>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={med.nome}
                      onChange={(e) => {
                        atualizarMedicamento(med.id, "nome", e.target.value);
                        buscarSugestoes(med.id, e.target.value);
                      }}
                      data-med-id={med.id}
                      placeholder="Digite o nome do medicamento..."
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "13px",
                        fontWeight: "600",
                        fontFamily: theme.fonts.sans,
                        boxSizing: "border-box",
                      }}
                    />
                    {/* DROPDOWN DE SUGESTÕES */}
                    {sugestoesMed.find((s) => s.id === med.id)?.sugestoes && (
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "white",
                          border: `1px solid ${theme.gray[300]}`,
                          borderRadius: theme.borderRadius.md,
                          boxShadow: theme.shadows.lg,
                          zIndex: 50,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {sugestoesMed
                          .find((s) => s.id === med.id)!
                          .sugestoes.map((sug, i) => (
                            <div
                              key={i}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const nomeCompleto = sug.apresentacao
                                  ? `${sug.nome} ${sug.apresentacao}`
                                  : sug.nome;
                                atualizarMedicamento(
                                  med.id,
                                  "nome",
                                  nomeCompleto
                                );
                                if (sug.posologiaSugerida && !med.posologia) {
                                  atualizarMedicamento(
                                    med.id,
                                    "posologia",
                                    sug.posologiaSugerida
                                  );
                                }
                                setSugestoesMed((prev) =>
                                  prev.filter((s) => s.id !== med.id)
                                );
                                salvarNaMemoria(nomeCompleto);
                              }}
                              style={{
                                padding: "6px 10px",
                                cursor: "pointer",
                                borderBottom: `1px solid ${theme.gray[100]}`,
                                fontSize: "12px",
                                fontFamily: theme.fonts.sans,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background =
                                  theme.gray[50])
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <span>
                                <strong>{sug.nome}</strong>
                                {sug.apresentacao && (
                                  <span
                                    style={{
                                      color: theme.gray[500],
                                      marginLeft: "4px",
                                    }}
                                  >
                                    {sug.apresentacao}
                                  </span>
                                )}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  flexShrink: 0,
                                }}
                              >
                                {(() => {
                                  // B356 (AI_154) — mostra o documento que a
                                  // Portaria 344/98 exige ANTES de escolher.
                                  const c = classificarMedicamento(
                                    sug.apresentacao
                                      ? `${sug.nome} ${sug.apresentacao}`
                                      : sug.nome
                                  );
                                  if (!c || c.documento === "semRetencao")
                                    return null;
                                  const talonario =
                                    !c.pergunta && exigeTalonario(c.documento);
                                  return (
                                    <span
                                      title={c.fundamento}
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        padding: "1px 6px",
                                        borderRadius: "8px",
                                        background: talonario
                                          ? "#fee2e2"
                                          : "#fef3c7",
                                        color: talonario ? "#991b1b" : "#92400e",
                                      }}
                                    >
                                      {c.pergunta
                                        ? `Lista ${c.lista} — depende da dose`
                                        : talonario
                                        ? `${NOME_DOCUMENTO[c.documento].replace("Notificação de Receita", "Notif.")} — talonário`
                                        : "Controle especial"}
                                    </span>
                                  );
                                })()}
                                <span
                                  style={{
                                    fontSize: "10px",
                                    color:
                                      sug.categoria === "Recente"
                                        ? "#16a34a"
                                        : theme.gray[400],
                                    fontStyle: "italic",
                                  }}
                                >
                                  {sug.categoria === "Recente"
                                    ? "Recente"
                                    : sug.categoria}
                                </span>
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <textarea
                    value={med.posologia}
                    onChange={(e) =>
                      atualizarMedicamento(med.id, "posologia", e.target.value)
                    }
                    placeholder="Posologia (ex: Tomar 1 cápsula via oral de 12/12 horas)"
                    rows={2}
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "12px",
                      fontFamily: theme.fonts.sans,
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                  />
                  {/* USO CONTÍNUO OU QUANTIDADE */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "11px",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <input
                        type="radio"
                        name={`tipoUso-${med.id}`}
                        checked={med.tipoUso === "continuo"}
                        onChange={() =>
                          atualizarMedicamento(
                            med.id,
                            "tipoUso" as any,
                            "continuo"
                          )
                        }
                      />
                      Uso contínuo
                    </label>
                    <label
                      style={{
                        fontSize: "11px",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <input
                        type="radio"
                        name={`tipoUso-${med.id}`}
                        checked={med.tipoUso === "quantidade"}
                        onChange={() =>
                          atualizarMedicamento(
                            med.id,
                            "tipoUso" as any,
                            "quantidade"
                          )
                        }
                      />
                      Quantidade:
                    </label>
                    {med.tipoUso === "quantidade" && (
                      <input
                        type="text"
                        value={med.quantidade || ""}
                        onChange={(e) =>
                          atualizarMedicamento(
                            med.id,
                            "quantidade" as any,
                            e.target.value
                          )
                        }
                        placeholder="Ex: 30 comprimidos"
                        style={{
                          padding: "3px 6px",
                          border: `1px solid ${theme.gray[300]}`,
                          borderRadius: theme.borderRadius.sm,
                          fontSize: "11px",
                          fontFamily: theme.fonts.sans,
                          width: "150px",
                        }}
                      />
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removerMedicamento(med.id)}
                  style={{
                    padding: "6px",
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    cursor: "pointer",
                    marginTop: "2px",
                  }}
                  title="Remover medicamento"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🏥 BANNER LME — medicamento de alto custo (CEAF) detectado */}
      {medsLME.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            padding: "10px 12px",
            background: dadosLME ? "#dcfce7" : "#eff6ff",
            border: `1px solid ${dadosLME ? "#16a34a" : "#3b82f6"}`,
            borderRadius: theme.borderRadius.md,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: dadosLME ? "#15803d" : "#1d4ed8",
                fontFamily: theme.fonts.sans,
              }}
            >
              🏥 Medicamento de alto custo (CEAF):{" "}
              {medsLME.map((m) => m.nome.split(",")[0]).join(", ")}
              {dadosLME ? " — LME preenchido ✓" : " — exige LME"}
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                onClick={abrirModalLME}
                style={{
                  padding: "5px 12px",
                  background: dadosLME ? "#16a34a" : theme.primary[600],
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {dadosLME ? "Editar LME" : "Preencher LME"}
              </button>
              {dadosLME && (
                <button
                  onClick={() => setDadosLME(null)}
                  style={{
                    padding: "5px 10px",
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "11px",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                  title="Remover o LME deste documento"
                >
                  Remover
                </button>
              )}
            </div>
          </div>
          <span
            style={{
              fontSize: "10.5px",
              color: theme.gray[500],
              fontFamily: theme.fonts.sans,
            }}
          >
            O LME sairá junto com a receita, no mesmo documento.
          </span>
        </div>
      )}

      {/* MODAL LME */}
      {lmeModalAberto && dadosLME && (
        <ModalLME
          dados={dadosLME}
          atualizar={atualizarLME}
          atualizarQtd={atualizarQtdLME}
          repetirMes1={repetirMes1LME}
          onFechar={() => setLmeModalAberto(false)}
          nomePaciente={paciente.nome}
        />
      )}

      {/* 🏥 BANNER HEPATITE B — antiviral do HBV detectado */}
      {medsHBVIds.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            padding: "10px 12px",
            background: dadosHBV ? "#dcfce7" : "#fff7ed",
            border: `1px solid ${dadosHBV ? "#16a34a" : "#f59e0b"}`,
            borderRadius: theme.borderRadius.md,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: dadosHBV ? "#15803d" : "#b45309",
                fontFamily: theme.fonts.sans,
              }}
            >
              🦠 Medicamento para Hepatite B:{" "}
              {medsHBVIds
                .map((id) => MEDICAMENTOS_HBV_CAMPO34.find((m) => m.id === id)?.rotulo.split(" ")[0])
                .join(", ")}
              {dadosHBV ? " — ficha preenchida ✓" : " — exige formulário de Hepatite B"}
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                onClick={abrirModalHBV}
                style={{
                  padding: "5px 12px",
                  background: dadosHBV ? "#16a34a" : "#d97706",
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {dadosHBV ? "Editar ficha" : "Preencher ficha"}
              </button>
              {dadosHBV && (
                <button
                  onClick={() => setDadosHBV(null)}
                  style={{
                    padding: "5px 10px",
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "11px",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                  title="Remover a ficha de Hepatite B deste documento"
                >
                  Remover
                </button>
              )}
            </div>
          </div>
          <span
            style={{
              fontSize: "10.5px",
              color: theme.gray[500],
              fontFamily: theme.fonts.sans,
            }}
          >
            A ficha de Hepatite B sairá junto com a receita, no mesmo documento.
          </span>
        </div>
      )}

      {/* MODAL HEPATITE B */}
      {hbvModalAberto && dadosHBV && (
        <ModalHepatiteB
          dados={dadosHBV}
          atualizar={atualizarHBV}
          alternar={alternarHBV}
          onFechar={() => setHbvModalAberto(false)}
          nomePaciente={paciente.nome}
        />
      )}

      {/* BOTÃO GERAR PDF */}
      <button
        onClick={handleGerarPDF}
        disabled={medicamentos.filter((m) => m.nome.trim()).length === 0}
        style={{
          padding: "10px 24px",
          background:
            medicamentos.filter((m) => m.nome.trim()).length === 0
              ? theme.gray[300]
              : "#16a34a",
          color: "white",
          border: "none",
          borderRadius: theme.borderRadius.md,
          fontSize: "14px",
          fontWeight: "700",
          cursor:
            medicamentos.filter((m) => m.nome.trim()).length === 0
              ? "not-allowed"
              : "pointer",
          fontFamily: theme.fonts.sans,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
        }}
      >
        <FileDown size={18} />{" "}
        {controleEspecial
          ? "Gerar Receituário + Controle Especial (PDF)"
          : "Gerar Receituário (PDF)"}
      </button>

      {/* B212 F4: ASSINAR DIGITALMENTE (médico-only + flag de habilitação) */}
      {ehMedico(usuarioLogado) && ASSINATURA_HABILITADA && (
        <button
          onClick={handleAssinarReceituario}
          disabled={medicamentos.filter((m) => m.nome.trim()).length === 0}
          style={{
            padding: "10px 24px",
            marginTop: "8px",
            background:
              medicamentos.filter((m) => m.nome.trim()).length === 0
                ? theme.gray[300]
                : theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "14px",
            fontWeight: "700",
            cursor:
              medicamentos.filter((m) => m.nome.trim()).length === 0
                ? "not-allowed"
                : "pointer",
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <ShieldCheck size={18} /> Assinar digitalmente
        </button>
      )}

      {/* B356 (AI_154) — numeração do SNCR ao lado da assinatura: 1ª obtenção e
          renovação abrem uma aba do gov.br via ticket (api/sncr-ticket) — sem login do
          app em hepato.app.br.
          Só afeta receita ASSINADA DIGITALMENTE; a impressa não usa numeração. */}
      {ehMedico(usuarioLogado) && ASSINATURA_HABILITADA && saldosSncr && (() => {
        const NOME: Record<TipoNumeracao, string> = {
          RCE: "controle especial",
          RET: "antimicrobiano/GLP-1",
        };
        const tipos = ["RCE", "RET"] as TipoNumeracao[];
        const faltando = tipos.filter((t) => !saldosSncr[t]?.total);
        const acabando = tipos.filter((t) => {
          const c = saldosSncr[t];
          return !!c && c.total > 0 && c.saldo < LIMIAR_SALDO_BAIXO;
        });
        const atencao = faltando.length > 0 || acabando.length > 0;
        // Rótulo diz o que falta: nunca "Renovar" com estoque cheio.
        const rotulo = faltando.length
          ? "Obter numeração (gov.br)"
          : "Renovar numeração";
        const partes = tipos.map((t) =>
          saldosSncr[t]?.total
            ? `${NOME[t]} ${saldosSncr[t]!.saldo} disponíveis`
            : `${NOME[t]}: nenhuma ainda`
        );
        return (
          <div
            style={{
              marginTop: "6px",
              padding: "8px 10px",
              background: atencao ? "#fffbeb" : theme.gray[50],
              border: `1px solid ${atencao ? "#f59e0b" : theme.gray[200]}`,
              borderRadius: theme.borderRadius.md,
              fontFamily: theme.fonts.sans,
              fontSize: "12px",
              color: theme.gray[700],
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span>
              <strong>Numeração SNCR</strong>
              {ambienteSncr() === "hmg" ? " (homologação)" : ""}: {partes.join(" · ")}.
              {faltando.length > 0 && (
                <span style={{ color: theme.gray[500] }}>
                  {" "}Necessária só para receita ASSINADA DIGITALMENTE; a impressa não precisa.
                </span>
              )}
            </span>
            {atencao && (
              <button
                type="button"
                onClick={() =>
                  abrirObtencaoPeloApp(
                    Array.from(new Set([...faltando, ...acabando])) as TipoNumeracao[]
                  )
                }
                style={{
                  padding: "4px 12px",
                  background: "#b45309",
                  color: "white",
                  border: "1px solid #b45309",
                  borderRadius: theme.borderRadius.sm,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: theme.fonts.sans,
                  fontSize: "12px",
                }}
              >
                {rotulo}
              </button>
            )}
          </div>
        );
      })()}

      {modalAssinatura && (
        <ModalAssinatura
          aberto={true}
          onFechar={handleFecharAssinatura}
          html={modalAssinatura.html}
          tipo={modalAssinatura.tipo}
          prescricaoId={modalAssinatura.prescricaoId}
          dados={modalAssinatura.dados}
          nomeArquivo={modalAssinatura.nomeArquivo}
          codigoValidacao={modalAssinatura.codigoValidacao}
          dadosValidacao={modalAssinatura.dadosValidacao}
        />
      )}
    </div>
  );
}

// ============================================
// 🏥 MODAL DO LME (Componente Especializado / alto custo)
// ============================================
// Modal COMPLETO: o LME exige preencher todos os campos (decisão de Leonardo,
// AI_97). Os dados do paciente vêm semeados do prontuário (editáveis); o médico
// completa peso/altura, raça/cor, CID/diagnóstico, anamnese, quantidades por
// mês e o atestado de capacidade. Os campos do solicitante (CNES, CNS, nome)
// saem do cadastro do usuário na geração, não aqui.
const MESES_LME = ["1º mês", "2º mês", "3º mês", "4º mês", "5º mês", "6º mês"];

function ModalLME({
  dados,
  atualizar,
  atualizarQtd,
  repetirMes1,
  onFechar,
  nomePaciente,
}: {
  dados: DadosLME;
  atualizar: (campo: keyof DadosLME, valor: any) => void;
  atualizarQtd: (idxMed: number, idxMes: number, valor: string) => void;
  repetirMes1: (idxMed: number) => void;
  onFechar: () => void;
  nomePaciente: string;
}) {
  // B280 (AI_120) — atalhos de diagnóstico conforme os medicamentos do LME:
  // estatinas/dislipidemia → CIDs E78x; demais (imunossupressor/AUDC) →
  // hepatologia/transplante. Receita só com estatina mostra apenas os E78x;
  // receita mista mostra os dois conjuntos.
  const temDislipidemia = dados.medicamentos.some((m) =>
    medicamentoLMEehDislipidemia(m.nome)
  );
  const temNaoDislipidemia = dados.medicamentos.some(
    (m) => m.nome.trim() && !medicamentoLMEehDislipidemia(m.nome)
  );
  const atalhosDiagnostico = [
    ...(temNaoDislipidemia || !temDislipidemia ? DIAGNOSTICOS_LME : []),
    ...(temDislipidemia ? DIAGNOSTICOS_LME_DISLIPIDEMIA : []),
  ];
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "6px 8px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: "12px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 600,
    color: theme.gray[600],
    fontFamily: theme.fonts.sans,
    marginBottom: "3px",
    display: "block",
  };
  const secaoTitulo: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.gray[700],
    fontFamily: theme.fonts.sans,
    margin: "4px 0 8px",
    borderBottom: `1px solid ${theme.gray[200]}`,
    paddingBottom: "4px",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onFechar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: theme.borderRadius.lg,
          width: "100%",
          maxWidth: "640px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: theme.shadows.lg,
        }}
      >
        {/* CABEÇALHO */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            borderBottom: `1px solid ${theme.gray[200]}`,
            padding: "14px 18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: theme.gray[400],
                fontFamily: theme.fonts.sans,
              }}
            >
              LME — Componente Especializado (alto custo)
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: theme.gray[800],
                fontFamily: theme.fonts.sans,
              }}
            >
              {nomePaciente}
            </div>
          </div>
          <button
            onClick={onFechar}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: theme.gray[500],
              lineHeight: 1,
            }}
            title="Fechar"
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* SEÇÃO 1 — DADOS DO PACIENTE */}
          <div>
            <div style={secaoTitulo}>1. Dados do Paciente</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div>
                <label style={labelStyle}>Nome da mãe *</label>
                <input
                  style={inputStyle}
                  value={dados.nomeMae}
                  onChange={(e) => atualizar("nomeMae", e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Peso (kg) *</label>
                  <input
                    style={inputStyle}
                    value={dados.pesoPaciente}
                    onChange={(e) => atualizar("pesoPaciente", e.target.value)}
                    placeholder="kg"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Altura (cm) *</label>
                  <input
                    style={inputStyle}
                    value={dados.alturaPaciente}
                    onChange={(e) => atualizar("alturaPaciente", e.target.value)}
                    placeholder="cm"
                  />
                </div>
                <div style={{ flex: 1.4 }}>
                  <label style={labelStyle}>Raça/Cor *</label>
                  <select
                    style={inputStyle}
                    value={dados.racaCor}
                    onChange={(e) => atualizar("racaCor", e.target.value)}
                  >
                    <option value="">Selecionar</option>
                    {OPCOES_RACA_COR.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {dados.racaCor === "Indígena" && (
                <div>
                  <label style={labelStyle}>Etnia (povo indígena)</label>
                  <input
                    style={inputStyle}
                    value={dados.etnia}
                    onChange={(e) => atualizar("etnia", e.target.value)}
                  />
                </div>
              )}
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Telefone</label>
                  <input
                    style={inputStyle}
                    value={dados.telefonePaciente}
                    onChange={(e) => atualizar("telefonePaciente", e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>E-mail</label>
                  <input
                    style={inputStyle}
                    value={dados.emailPaciente}
                    onChange={(e) => atualizar("emailPaciente", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Documento do paciente (campo 21)</label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", fontFamily: theme.fonts.sans }}>
                  {(["CPF", "CNS"] as const).map((t) => (
                    <label key={t} style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="lme-doc"
                        checked={dados.docTipo === t}
                        onChange={() => atualizar("docTipo", t)}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  Paciente tem capacidade de ir buscar o medicamento? (campo 13 — atestado de capacidade)
                </label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", fontFamily: theme.fonts.sans }}>
                  {/* capaz de buscar = NÃO é incapaz; o laudo imprime a pergunta oficial invertida */}
                  {([
                    { rotulo: "Sim", incapaz: "nao" },
                    { rotulo: "Não", incapaz: "sim" },
                  ] as const).map((op) => (
                    <label key={op.rotulo} style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="lme-incapaz"
                        checked={dados.incapaz === op.incapaz}
                        onChange={() => atualizar("incapaz", op.incapaz)}
                      />
                      {op.rotulo}
                    </label>
                  ))}
                </div>
                {dados.incapaz === "sim" && (
                  <input
                    style={{ ...inputStyle, marginTop: "6px" }}
                    value={dados.nomeResponsavel}
                    onChange={(e) => atualizar("nomeResponsavel", e.target.value)}
                    placeholder="Nome do responsável que poderá retirar o medicamento"
                  />
                )}
              </div>
            </div>
          </div>

          {/* SEÇÃO 2 — MEDICAMENTOS / QUANTIDADES */}
          <div>
            <div style={secaoTitulo}>2. Medicamentos e quantidade por mês</div>
            {dados.medicamentos.length === 0 ? (
              <div style={{ fontSize: "12px", color: theme.gray[500], fontFamily: theme.fonts.sans }}>
                Nenhum medicamento de alto custo na receita.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {dados.medicamentos.map((m, idxMed) => (
                  <div
                    key={idxMed}
                    style={{
                      border: `1px solid ${theme.gray[200]}`,
                      borderRadius: theme.borderRadius.md,
                      padding: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: theme.gray[700],
                        fontFamily: theme.fonts.sans,
                        marginBottom: "6px",
                      }}
                    >
                      {m.nome}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "6px" }}>
                      {MESES_LME.map((mes, idxMes) => (
                        <div key={idxMes}>
                          <label
                            style={{
                              fontSize: "9.5px",
                              color: theme.gray[500],
                              fontFamily: theme.fonts.sans,
                              display: "block",
                              textAlign: "center",
                              marginBottom: "2px",
                            }}
                          >
                            {mes}
                            {idxMes < 3 ? " *" : ""}
                          </label>
                          <input
                            style={{ ...inputStyle, padding: "5px 4px", textAlign: "center" }}
                            value={m.quantidades[idxMes] || ""}
                            onChange={(e) => atualizarQtd(idxMed, idxMes, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => repetirMes1(idxMed)}
                      style={{
                        marginTop: "6px",
                        padding: "3px 8px",
                        background: theme.gray[100],
                        color: theme.gray[600],
                        border: "none",
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "10.5px",
                        cursor: "pointer",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      ↳ repetir o mês 1 nos 6 meses
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEÇÃO 3 — JUSTIFICATIVA */}
          <div>
            <div style={secaoTitulo}>3. Justificativa</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div>
                <label style={labelStyle}>Diagnóstico (atalho)</label>
                <select
                  style={inputStyle}
                  value={
                    atalhosDiagnostico.find((d) => d.rotulo === dados.diagnostico)
                      ?.rotulo || ""
                  }
                  onChange={(e) => {
                    const d = atalhosDiagnostico.find(
                      (x) => x.rotulo === e.target.value
                    );
                    if (d) {
                      atualizar("diagnostico", d.rotulo);
                      atualizar("cid10", d.cid);
                    }
                  }}
                >
                  <option value="">Selecionar (ou digite abaixo)</option>
                  {atalhosDiagnostico.map((d) => (
                    <option key={d.cid} value={d.rotulo}>
                      {d.rotulo} ({d.cid})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>CID-10 *</label>
                  <input
                    style={inputStyle}
                    value={dados.cid10}
                    onChange={(e) => atualizar("cid10", e.target.value)}
                    placeholder="Ex.: Z94.4"
                  />
                </div>
                <div style={{ flex: 3 }}>
                  <label style={labelStyle}>Diagnóstico (texto)</label>
                  <input
                    style={inputStyle}
                    value={dados.diagnostico}
                    onChange={(e) => atualizar("diagnostico", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Anamnese *</label>
                <textarea
                  style={{ ...inputStyle, resize: "vertical" }}
                  rows={3}
                  value={dados.anamnese}
                  onChange={(e) => atualizar("anamnese", e.target.value)}
                  placeholder="Breve história clínica que justifica a solicitação."
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Paciente realizou tratamento prévio ou está em tratamento? (campo 12)
                </label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", fontFamily: theme.fonts.sans }}>
                  {(["nao", "sim"] as const).map((v) => (
                    <label key={v} style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="lme-trat"
                        checked={dados.tratamentoPrevio === v}
                        onChange={() => atualizar("tratamentoPrevio", v)}
                      />
                      {v === "nao" ? "Não" : "Sim"}
                    </label>
                  ))}
                </div>
                {dados.tratamentoPrevio === "sim" && (
                  <input
                    style={{ ...inputStyle, marginTop: "6px" }}
                    value={dados.tratamentoPrevioRelato}
                    onChange={(e) => atualizar("tratamentoPrevioRelato", e.target.value)}
                    placeholder="Relatar o tratamento."
                  />
                )}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Campos do paciente preenchidos por (campo 18)</label>
                  <select
                    style={inputStyle}
                    value={dados.preenchidoPor}
                    onChange={(e) => atualizar("preenchidoPor", e.target.value)}
                  >
                    {OPCOES_PREENCHIDO_POR.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Data da solicitação *</label>
                  <input
                    style={inputStyle}
                    value={dados.dataSolicitacao}
                    onChange={(e) => atualizar("dataSolicitacao", e.target.value)}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RODAPÉ */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: `1px solid ${theme.gray[200]}`,
            padding: "12px 18px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <button
            onClick={onFechar}
            style={{
              padding: "8px 20px",
              background: "#16a34a",
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            Salvar e fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 🏥 MODAL HEPATITE B — ficha completa (todos os campos 1–36)
// ============================================================
function ModalHepatiteB({
  dados,
  atualizar,
  alternar,
  onFechar,
  nomePaciente,
}: {
  dados: DadosHepatiteB;
  atualizar: (campo: keyof DadosHepatiteB, valor: any) => void;
  alternar: (campo: keyof DadosHepatiteB, opcao: string) => void;
  onFechar: () => void;
  nomePaciente: string;
}) {
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "6px 8px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: "12px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 600,
    color: theme.gray[600],
    fontFamily: theme.fonts.sans,
    marginBottom: "3px",
    display: "block",
  };
  const secaoTitulo: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.gray[700],
    fontFamily: theme.fonts.sans,
    margin: "4px 0 8px",
    borderBottom: `1px solid ${theme.gray[200]}`,
    paddingBottom: "4px",
  };
  const optLabel: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: "5px",
    cursor: "pointer",
    fontSize: "11.5px",
    fontFamily: theme.fonts.sans,
    color: theme.gray[700],
  };

  // Grupo de radio (escolha única). vazioPermitido: clicar de novo limpa.
  const radioGroup = (
    campo: keyof DadosHepatiteB,
    opcoes: readonly string[],
    coluna = false
  ) => (
    <div
      style={{
        display: "flex",
        flexDirection: coluna ? "column" : "row",
        flexWrap: "wrap",
        gap: coluna ? "4px" : "12px",
      }}
    >
      {opcoes.map((o) => (
        <label key={o} style={optLabel}>
          <input
            type="radio"
            checked={(dados[campo] as string) === o}
            onChange={() => atualizar(campo, (dados[campo] as string) === o ? "" : o)}
            onClick={() => {
              if ((dados[campo] as string) === o) atualizar(campo, "");
            }}
          />
          {o}
        </label>
      ))}
    </div>
  );

  // Grupo de checkbox (múltipla escolha).
  const checkGroup = (
    campo: keyof DadosHepatiteB,
    opcoes: readonly string[],
    coluna = true
  ) => (
    <div
      style={{
        display: "flex",
        flexDirection: coluna ? "column" : "row",
        flexWrap: "wrap",
        gap: coluna ? "4px" : "12px",
      }}
    >
      {opcoes.map((o) => (
        <label key={o} style={optLabel}>
          <input
            type="checkbox"
            checked={((dados[campo] as string[]) || []).includes(o)}
            onChange={() => alternar(campo, o)}
          />
          {o}
        </label>
      ))}
    </div>
  );

  const campoBox = (titulo: string, conteudo: React.ReactNode) => (
    <div>
      <label style={labelStyle}>{titulo}</label>
      {conteudo}
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onFechar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: theme.borderRadius.lg,
          width: "100%",
          maxWidth: "720px",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: theme.shadows.lg,
        }}
      >
        {/* CABEÇALHO */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            borderBottom: `1px solid ${theme.gray[200]}`,
            padding: "14px 18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: theme.gray[400],
                fontFamily: theme.fonts.sans,
              }}
            >
              Solicitação de Medicamentos — Hepatite B
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: theme.gray[800],
                fontFamily: theme.fonts.sans,
              }}
            >
              {nomePaciente}
            </div>
          </div>
          <button
            onClick={onFechar}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: theme.gray[500],
              lineHeight: 1,
            }}
            title="Fechar"
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: "18px" }}>
          {/* SEÇÃO 1 — IDENTIFICAÇÃO */}
          <div>
            <div style={secaoTitulo}>1. Identificação</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>SINAN (campo 3)</label>
                  <input style={inputStyle} value={dados.sinan} onChange={(e) => atualizar("sinan", e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Prontuário (campo 4)</label>
                  <input style={inputStyle} value={dados.prontuario} onChange={(e) => atualizar("prontuario", e.target.value)} />
                </div>
                <div style={{ flex: 1.4 }}>
                  <label style={labelStyle}>Nome social (campo 6)</label>
                  <input style={inputStyle} value={dados.nomeSocial} onChange={(e) => atualizar("nomeSocial", e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: "10.5px", color: theme.gray[400], fontFamily: theme.fonts.sans }}>
                Nome, CPF e CNS do paciente saem do cadastro automaticamente.
              </div>
            </div>
          </div>

          {/* SEÇÃO 2 — DADOS CLÍNICOS */}
          <div>
            <div style={secaoTitulo}>2. Dados clínicos</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("7. Início de tratamento? *", radioGroup("inicioTratamento", ["Sim", "Não"]))}
                {campoBox("9. Origem do acompanhamento *", radioGroup("origemAcompanhamento", ["Público", "Privado"]))}
                <div>
                  <label style={labelStyle}>10. Peso (kg) *</label>
                  <input style={{ ...inputStyle, width: "90px" }} value={dados.pesoPaciente} onChange={(e) => atualizar("pesoPaciente", e.target.value)} />
                </div>
              </div>
              {campoBox("8. Este formulário é válido por: *", radioGroup("validadePor", VALIDADE_OPCOES))}
              {campoBox("11. CID-10 *", radioGroup("cid10", CIDS_HBV))}
              <div>
                <label style={labelStyle}>12. Paciente coinfectado? *</label>
                <label style={{ ...optLabel, marginBottom: "4px" }}>
                  <input
                    type="checkbox"
                    checked={dados.semCoinfeccao}
                    onChange={() => atualizar("semCoinfeccao", !dados.semCoinfeccao)}
                  />
                  Sem Coinfecção
                </label>
                {checkGroup("coinfeccoes", COINFECCOES_OPCOES, false)}
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("13. Exame de HBeAg *", radioGroup("hbeag", ["Reagente", "Não Reagente"]))}
                {campoBox("14. Exame de ALT? *", radioGroup("alt", ["Maior que 1,5 vezes LSN", "Menor que 1,5 vezes LSN"], true))}
                {campoBox("16. Gravidade de cirrose (Child-Pugh) *", radioGroup("gravidadeCirrose", ["Sem Cirrose", "Child A", "Child B ou C"], true))}
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <label style={labelStyle}>15. HBV-DNA — Resultado (UI/mL) *</label>
                  <input style={inputStyle} value={dados.hbvDnaResultado} onChange={(e) => atualizar("hbvDnaResultado", e.target.value)} />
                </div>
                <div style={{ width: "130px" }}>
                  <label style={labelStyle}>Data do HBV-DNA</label>
                  <input style={inputStyle} value={dados.hbvDnaData} onChange={(e) => atualizar("hbvDnaData", e.target.value)} placeholder="dd/mm/aaaa" />
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("17. Exame de anti-HDV IgG", radioGroup("antiHDV", ["Reagente", "Não Reagente"]))}
                {campoBox("18. Exame de HDV-RNA", radioGroup("hdvRna", ["Detectável", "Não detectável"]))}
              </div>
              <div style={{ fontSize: "10px", color: theme.gray[400], fontFamily: theme.fonts.sans, marginTop: "-6px" }}>
                Campos 17 e 18 obrigatórios para CIDs B16.0, B16.1, B17.0 e B18.0.
              </div>
              {campoBox("19. Resultado Beta-HCG? *", radioGroup("betaHCG", BETA_HCG_OPCOES, true))}
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <label style={labelStyle}>20. Diagnóstico materno — Data (se beta-HCG +)</label>
                  <input style={inputStyle} value={dados.diagMaternoData} onChange={(e) => atualizar("diagMaternoData", e.target.value)} placeholder="dd/mm/aaaa" />
                </div>
                <div style={{ width: "150px" }}>
                  <label style={labelStyle}>Idade gestacional (semanas)</label>
                  <input style={inputStyle} value={dados.diagMaternoIG} onChange={(e) => atualizar("diagMaternoIG", e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* SEÇÃO 3 — INDICAÇÃO E PROFILAXIA */}
          <div>
            <div style={secaoTitulo}>3. Indicação terapêutica e profilaxia</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {campoBox("21. Indicação terapêutica: *", radioGroup("indicacaoTerapeutica", INDICACOES_TERAPEUTICAS, true))}
              {campoBox("22. Situação para profilaxia da reativação viral:", radioGroup("situacaoProfilaxia", SITUACAO_PROFILAXIA_OPCOES, true))}
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <div>
                  <label style={labelStyle}>23. Doador — HBsAg</label>
                  {radioGroup("doadorHBsAg", ["Reagente", "Não Reagente"], true)}
                </div>
                <div>
                  <label style={labelStyle}>23. Doador — Anti-HBc total</label>
                  {radioGroup("doadorAntiHBc", ["Reagente", "Não Reagente"], true)}
                </div>
                <div>
                  <label style={labelStyle}>24. Receptor — HBsAg</label>
                  {radioGroup("receptorHBsAg", ["Reagente", "Não Reagente"], true)}
                </div>
                <div>
                  <label style={labelStyle}>24. Receptor — Anti-HBc total</label>
                  {radioGroup("receptorAntiHBc", ["Reagente", "Não Reagente"], true)}
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("25. Risco de reativação viral:", radioGroup("riscoReativacao", RISCO_REATIVACAO_OPCOES))}
                {campoBox("26. Momento da profilaxia da reinfecção:", radioGroup("momentoProfilaxia", MOMENTO_PROFILAXIA_OPCOES, true))}
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("27. Risco de reinfecção pós-transplante:", radioGroup("riscoReinfeccao", RISCO_REINFECCAO_OPCOES))}
                {campoBox("28. Necessidade de terapia preemptiva:", radioGroup("terapiaPreemptiva", ["Sim", "Não"]))}
              </div>
            </div>
          </div>

          {/* SEÇÃO 4 — CRITÉRIOS */}
          <div>
            <div style={secaoTitulo}>4. Critérios e histórico</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {campoBox("29. Outros critérios para indicação de tratamento: *", checkGroup("outrosCriterios", OUTROS_CRITERIOS_OPCOES))}
              {campoBox("30. O paciente apresenta alguma destas condições? *", checkGroup("condicoes", CONDICOES_OPCOES))}
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {campoBox("31. Contraindicação ao uso de: *", checkGroup("contraindicacao", CONTRAINDICACAO_OPCOES))}
                {campoBox("32. Resistência ou falha terapêutica ao uso de: *", checkGroup("resistenciaFalha", RESISTENCIA_OPCOES))}
                {campoBox("33. Uso prévio de: *", checkGroup("usoPrevio", USO_PREVIO_OPCOES))}
              </div>
            </div>
          </div>

          {/* SEÇÃO 5 — MEDICAMENTOS E FINALIZAÇÃO */}
          <div>
            <div style={secaoTitulo}>5. Medicamentos (campo 34) e finalização</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ fontSize: "10.5px", color: theme.gray[400], fontFamily: theme.fonts.sans }}>
                Os antivirais prescritos já vêm marcados. Marque/desmarque conforme o esquema.
              </div>
              {MEDICAMENTOS_HBV_CAMPO34.map((m) => (
                <div key={m.id} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={optLabel}>
                    <input
                      type="checkbox"
                      checked={dados.medicamentos.includes(m.id)}
                      onChange={() => alternar("medicamentos", m.id)}
                    />
                    <span>
                      <b>{m.rotulo}</b>
                      <span style={{ color: theme.gray[400], fontSize: "10.5px" }}> — {m.posologia}</span>
                    </span>
                  </label>
                  {m.id === "entecavir" && dados.medicamentos.includes("entecavir") && (
                    <input
                      style={{ ...inputStyle, width: "240px", marginLeft: "22px" }}
                      value={dados.entecavirComprimidos}
                      onChange={(e) => atualizar("entecavirComprimidos", e.target.value)}
                      placeholder="Nº de comprimidos por dia (ex.: 1)"
                    />
                  )}
                </div>
              ))}
              <div>
                <label style={labelStyle}>35. Observação</label>
                <textarea
                  style={{ ...inputStyle, resize: "vertical" }}
                  rows={2}
                  value={dados.observacao}
                  onChange={(e) => atualizar("observacao", e.target.value)}
                />
              </div>
              <div style={{ width: "180px" }}>
                <label style={labelStyle}>36. Data da solicitação *</label>
                <input
                  style={inputStyle}
                  value={dados.dataSolicitacao}
                  onChange={(e) => atualizar("dataSolicitacao", e.target.value)}
                  placeholder="dd/mm/aaaa"
                />
              </div>
            </div>
          </div>
        </div>

        {/* RODAPÉ */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: `1px solid ${theme.gray[200]}`,
            padding: "12px 18px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <button
            onClick={onFechar}
            style={{
              padding: "8px 20px",
              background: "#16a34a",
              color: "white",
              border: "none",
              borderRadius: theme.borderRadius.md,
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            Salvar e fechar
          </button>
        </div>
      </div>
    </div>
  );
}
